// Enhanced Vegas-Style FruitScatterSlots.js - With realistic casino elements
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Avatar from "../../components/Avatar";
import LiveWinNotifications from "../../components/LiveWinNotifications";
import { CommunityJackpotModal } from '../../components/Casino/CommunityJackpotModal';
import { Fragment } from "react";
import { 
  BanknotesIcon, 
  ArrowTrendingUpIcon,
  InformationCircleIcon,
  UserIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  LockClosedIcon,
  BeakerIcon,
  GiftIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  WalletIcon
} from "@heroicons/react/24/solid";
import { VerificationHistoryModal } from "../../components/VerificationHistoryModal";
import SleekPaylinesVisualizer from "../../components/SleekPaylinesVisualizer";

// Audio file paths (these would be actual files in your project)
const AUDIO_PATHS = {
  REEL_STOP: '/sounds/casino/reel_stop.mp3',
  SPIN_START: '/spin.m4a',
  BUTTON_CLICK: '/sounds/casino/button_click.mp3',
  WIN_SMALL: '/win.wav',
  WIN_MEDIUM: '/win.wav',
  WIN_BIG: '/win.wav',
  WIN_MEGA: '/win.wav',
  COINS_BURST: '/sounds/casino/coins_burst.mp3',
  FREE_SPINS: '/sounds/casino/free_spins.mp3',
  AMBIENT: '/sounds/casino/ambient_casino.mp3',
  ERROR: '/sounds/casino/error.mp3'
};

export default function FruitScatterSlots() {

  const CURRENT_VERSION = "1.0.2";

  // User state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeWinLine, setActiveWinLine] = useState(null);

  // Modals
  const [showVerificationHistory, setShowVerificationHistory] = useState(false);
  const [showPayline, setShowPayline] = useState(false);
  const [showPromotions, setShowPromotions] = useState(false);
  const [newPromotion, setNewPromotion] = useState(true);

  const [communityPotAmount, setCommunityPotAmount] = useState(0);
  const [lastLostAmount, setLastLostAmount] = useState(0);
  const [showLostFeedback, setShowLostFeedback] = useState(false);

  // Game state
  const [spinning, setSpinning] = useState(false);
  const [balance, setBalance] = useState(0);
  const [betAmount, setBetAmount] = useState(1.00);
  const [winAmount, setWinAmount] = useState(0);
  const [lastWin, setLastWin] = useState(0);
  const [freeSpins, setFreeSpins] = useState(() => {
    const savedFreeSpins = localStorage.getItem('freeSpins');
    return savedFreeSpins ? parseInt(savedFreeSpins, 10) : 0;
  });
  const [freeSpinBetAmount, setFreeSpinBetAmount] = useState(() => {
    const savedAmount = localStorage.getItem('freeSpinBetAmount');
    return savedAmount ? parseFloat(savedAmount) : null;
  });
  const [freeSpinInProgress, setFreeSpinInProgress] = useState(false);
  const [showPaytable, setShowPaytable] = useState(false);
  const [showUserStats, setShowUserStats] = useState(false);
  const [fairnessProof, setFairnessProof] = useState(null);
  const [showFairnessModal, setShowFairnessModal] = useState(false);
  const [winLines, setWinLines] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [reelStopSequence, setReelStopSequence] = useState([]);
  const [progressiveJackpot, setProgressiveJackpot] = useState(12487.56);
  const [vipLevel, setVipLevel] = useState(2); // 0-5 for VIP levels
  const [pendingWin, setPendingWin] = useState(null);

  const [showCommunityPotModal, setShowCommunityPotModal] = useState(false);

  const [isDailyBonusClaimed, setIsDailyBonusClaimed] = useState(false);
  const [nextDailyBonusTime, setNextDailyBonusTime] = useState(null);

  // Audio references
  const audioRefs = useRef({});
  const ambientAudioRef = useRef(null);
  
  const spinSoundRef = useRef(null);

  const [sessionWins, setSessionWins] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionWinCap, setSessionWinCap] = useState(50); // Cap at 50x total bet

  const [consecutiveWins, setConsecutiveWins] = useState(0);
  const [streakBreakerActive, setStreakBreakerActive] = useState(false);

  // References for animations
  const machineRef = useRef(null);
  const reelsRef = useRef(null);
  const marqueeRef = useRef(null);
  
  // Celebration state
  const [showCelebrationOverlay, setShowCelebrationOverlay] = useState(false);
  const [celebrationType, setCelebrationType] = useState(''); // 'win', 'nowin', 'freespin', 'error'
  const [celebrationData, setCelebrationData] = useState(null);

  const [showJackpotModal, setShowJackpotModal] = useState(false);
  const [jackpotInfo, setJackpotInfo] = useState({
    totalAmount: 0,
    dailyAmount: 0,
    lastDistributionDate: null
  });
  const [userJackpotContributions, setUserJackpotContributions] = useState({
    total: 0,
    today: 0,
    sharePercentage: 0,
    estimatedReturn: 0,
    lastDistribution: 0,
    yourLastShare: 0
  });
  
  const symbols = [
    { name: "seven", icon: "7️⃣", value: [0, 0, 1.5, 3, 2],  color: "red" },           // Reduced from [0, 0, 2.5, 8, 20]
    { name: "cherry", icon: "🍒", value: [0, 0, 1, 2, 1.8],   color: "red" },       // Reduced from [0, 0, 1.8, 4.5, 12]
    { name: "lemon", icon: "🍋", value: [0, 0, 0.7, 1.4, 2.3],  color: "yellow" },        // Reduced from [0, 0, 1.2, 3.2, 10]
    { name: "watermelon", icon: "🍉", value: [0, 0, 0.4, 1, 1.8],   color: "green" },    // Reduced from [0, 0, 0.8, 2.5, 8]
    { name: "orange", icon: "🍊", value: [0, 0, 0.2, 0.4, 1.5],   color: "orange" },       // Reduced from [0, 0, 0.4, 1.5, 6]
    { name: "plum", icon: "🍇", value: [0, 0, 0.1, 0.4, 1.5], color: "purple" },    // Reduced from [0, 0, 0.2, 0.8, 3]
    { name: "scatter", icon: "scatter-star", value: [0, 0, 0.5, 3, 8], color: "gold" }, // Reduced from [0, 0, 0.8, 4, 12]
    { name: "wild", icon: "🃏", value: [0, 0, 2, 6, 20],  color: "rainbow" }          // Reduced from [0, 0, 4, 12, 40]
  ];

  // Mapping symbol names to their styles
  const symbolStyles = {
    "seven": { bg: "bg-pink-600", border: "border-pink-400", icon: "7️⃣" },
    "cherry": { bg: "bg-pink-500", border: "border-pink-300", icon: "🍒" },
    "lemon": { bg: "bg-yellow-400", border: "border-yellow-300", icon: "🍋" },
    "watermelon": { bg: "bg-green-500", border: "border-green-400", icon: "🍉" },
    "orange": { bg: "bg-orange-400", border: "border-orange-300", icon: "🍊" },
    "plum": { bg: "bg-purple-500", border: "border-purple-300", icon: "🍇" },
    // Update scatter to use an image instead of an emoji
    "scatter": { 
      bg: "bg-transparent", 
      border: "border-pink-300", 
      icon: "scatter-star",  // This marker will be used to identify where to insert the image
      imageSrc: "/scatter-star.png" // Path to your image
    },
    "wild": { bg: "bg-gradient-to-r from-violet-500 via-pink-500 to-violet-500", border: "border-violet-300", icon: "🃏" }
  };
  
  // Casino messages for marquee
  const casinoMessages = [
    "🎰 Welcome to Quantum Slots! Good luck!",
    "⭐ 3+ SCATTER symbols award FREE SPINS!",
    "🃏 WILD substitutes for any symbol except SCATTER",
    // "🏆 TOP JACKPOT: 2000x YOUR BET!",
    // "💰 Progressive Jackpot growing every minute!",
    "🎲 Play responsibly. Know your limits.",
    "🔒 Powered by Quantum RNG - Guaranteed Fair"
  ];
  
  // VIP tiers
  const vipTiers = [
    { name: "Bronze", color: "from-amber-600 to-amber-700", bonus: "5%" },
    { name: "Silver", color: "from-gray-400 to-gray-500", bonus: "10%" },
    { name: "Gold", color: "from-yellow-400 to-yellow-500", bonus: "15%" },
    { name: "Platinum", color: "from-cyan-400 to-cyan-500", bonus: "20%" },
    { name: "Diamond", color: "from-blue-400 to-indigo-500", bonus: "25%" },
    { name: "Royal", color: "from-purple-500 to-pink-500", bonus: "30%" }
  ];
  
  // Grid configuration - 5 columns by 3 rows (standard slot layout)
  const cols = 5;
  const rows = 3;
  
  // Initialize the grid
  const [grid, setGrid] = useState(Array(rows).fill().map(() => 
    Array(cols).fill("cherry") // Initial placeholder
  ));
  
  // Initialize payline visibility
  const [visiblePaylines, setVisiblePaylines] = useState([]);
  
  const paylines = [
    // ─── Horizontals ──────────────────────────────────────────────────────────
    { id: 0, positions: [[0,0],[0,1],[0,2],[0,3],[0,4]], color: "from-red-500 to-pink-500",    name: "top-row"    },
    { id: 1, positions: [[1,0],[1,1],[1,2],[1,3],[1,4]], color: "from-blue-500 to-indigo-500", name: "middle-row" },
    { id: 2, positions: [[2,0],[2,1],[2,2],[2,3],[2,4]], color: "from-green-500 to-emerald-500",name: "bottom-row" },
  
    // ─── Snake Diagonals ───────────────────────────────────────────────────────
    { id: 3, positions: [[0,0],[1,1],[2,2],[1,3],[0,4]], color: "from-purple-500 to-violet-500",name: "diag-TL→BR"   },
    { id: 4, positions: [[2,0],[1,1],[0,2],[1,3],[2,4]], color: "from-yellow-500 to-amber-500", name: "diag-BL→TR"   },
  
    // ─── V-Shapes ──────────────────────────────────────────────────────────────
    { id: 5, positions: [[0,0],[0,1],[1,2],[2,3],[2,4]], color: "from-cyan-500 to-blue-500",    name: "V-down"       },
    { id: 6, positions: [[2,0],[2,1],[1,2],[0,3],[0,4]], color: "from-orange-500 to-red-500",   name: "V-up"         },
  
    // ─── Zig-Zags ─────────────────────────────────────────────────────────────
    { id: 7, positions: [[0,0],[1,1],[0,2],[1,3],[0,4]], color: "from-emerald-500 to-green-500",name: "zigzag-top"   },
    { id: 8, positions: [[2,0],[1,1],[2,2],[1,3],[2,4]], color: "from-pink-500 to-rose-500",   name: "zigzag-bottom"},
    { id: 9, positions: [[1,0],[0,1],[1,2],[2,3],[1,4]], color: "from-amber-500 to-yellow-500",name: "zigzag-middle"},
  ];

  // Add this effect to force refresh when game version changes
  useEffect(() => {
    const storedVersion = localStorage.getItem('slotGameVersion');
    
    // If version is different or not set, force refresh and reset game data
    if (!storedVersion || storedVersion !== CURRENT_VERSION) {
      console.log(`Game updated from ${storedVersion || 'initial'} to ${CURRENT_VERSION}`);
      
      // Clear all cached game data
      localStorage.removeItem('freeSpins');
      localStorage.removeItem('freeSpinBetAmount');
      localStorage.setItem('slotGameVersion', CURRENT_VERSION);
      
      // Force a complete refresh (bypassing cache)
      if (storedVersion) { // Only refresh if there was a previous version (not first load)
        alert("Game has been updated with new features! Refreshing...");
        window.location.reload(true);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('freeSpins', freeSpins.toString());
  }, [freeSpins]);  
  
  // Setup progressive jackpot ticker
  useEffect(() => {
    const jackpotInterval = setInterval(() => {
      setProgressiveJackpot(prev => {
        // Increase by a small random amount
        const increase = Math.random() * 0.25;
        return parseFloat((prev + increase).toFixed(2));
      });
    }, 5000); // Update every 5 seconds
    
    return () => clearInterval(jackpotInterval);
  }, []);

  // Audio setup effect
  useEffect(() => {
    // Create and preload audio elements
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      if (key === 'AMBIENT') {
        audio.loop = true;
        audio.volume = 0.3;
        ambientAudioRef.current = audio;
      } else if (key === 'SPIN_START') {
        // Handle spin sound specially
        audio.loop = true; // Make spin sound loop until explicitly stopped
        audio.volume = 0.5;
        spinSoundRef.current = audio;
        audioRefs.current[key] = audio; // Also store in audioRefs for consistency
      } else {
        // For other sounds, set initial volume
        if (key.includes('WIN')) {
          audio.volume = 0.7;
        } else if (key === 'ERROR') {
          audio.volume = 0.6;
        } else {
          audio.volume = 0.5;
        }
        audioRefs.current[key] = audio;
      }
    });
    
    // Start ambient sound if enabled
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    }
    
    return () => {
      // Cleanup
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
      }
      
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
      });
      audioRefs.current = {};
    };
  }, []);
  
  // Effect for sound toggle
  useEffect(() => {
    if (ambientAudioRef.current) {
      if (soundEnabled) {
        ambientAudioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      } else {
        ambientAudioRef.current.pause();
      }
    }
  }, [soundEnabled]);

  useEffect(() => {
    fetchJackpotInfo();
  }, []);

  const saveFreeSpinsToServer = async (freeSpinsCount) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await axios.post('/api/casino/user/free-spins', {
        freeSpins: freeSpinsCount
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Successfully saved free spins to server:', freeSpinsCount);
    } catch (error) {
      console.error('Failed to save free spins to server:', error);
      // We don't show an error to the user because the spins will still
      // work locally via localStorage, and we can try to sync next time
    }
  };

  const fetchCommunityPotAmount = async () => {
    try {
      const response = await axios.get('/api/casino/community-pot');
      if (response.data.success) {
        setCommunityPotAmount(response.data.amount);
      }
    } catch (error) {
      console.error('Failed to fetch community pot amount:', error);
    }
  };

  const handleClaimDailyBonus = async () => {
    try {
      playSound('BUTTON_CLICK');
      
      const token = localStorage.getItem('token');
      if (!token) {
        // Handle not logged in
        setError("Please log in to claim your daily bonus");
        return;
      }
      
      const response = await axios.post('/api/casino/claim-daily-bonus', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Update local state
        setBalance(response.data.newBalance);
        setIsDailyBonusClaimed(true);
        setNextDailyBonusTime(new Date(response.data.nextClaimTime));
        
        // Show success message
        setCelebrationType('win');
        setCelebrationData({ amount: response.data.bonusAmount, message: 'Daily Bonus Claimed!' });
        setShowCelebrationOverlay(true);
        
        // Play win sound
        playSound('WIN_SMALL');
        
        // Hide after a moment
        setTimeout(() => {
          setShowCelebrationOverlay(false);
          setShowPromotions(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to claim daily bonus:', error);
      
      // Check for already claimed error
      if (error.response && error.response.data && error.response.data.nextClaimTime) {
        setNextDailyBonusTime(new Date(error.response.data.nextClaimTime));
        setIsDailyBonusClaimed(true);
      }
      
      // Show error message
      handleError(error.response?.data?.error || "Failed to claim bonus");
      playSound('ERROR');
    }
  };


  // Add this to your useEffect that sets up the game
useEffect(() => {
  // Check for interrupted spins on component mount
  const checkForInterruptedSpin = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const userId = extractUserIdFromToken(token);
    if (!userId) return;
    
    try {
      const response = await axios.get(`/api/casino/quantum/last-spin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success && response.data.pendingSpin) {
        const pendingSpin = response.data.pendingSpin;
        
        // Only recover if the spin is recent (within last 5 minutes)
        const spinTime = new Date(pendingSpin.timestamp);
        const now = new Date();
        const timeDiff = (now - spinTime) / 1000 / 60; // in minutes
        
        if (timeDiff <= 5) {
          console.log('Recovering interrupted spin from server');
          
          // Update your state with the pending spin result
          setGrid(pendingSpin.grid);
          setBalance(response.data.currentBalance);
          
          // Show a message to the user
          setCelebrationType('info');
          setCelebrationData({ message: 'Recovered your previous spin result!' });
          setShowCelebrationOverlay(true);
          
          // Process the win if applicable
          if (pendingSpin.payout > 0) {
            setTimeout(() => {
              setShowCelebrationOverlay(false);
              
              // Create a win data object for handleWinReveal
              const winData = {
                amount: pendingSpin.payout,
                lines: pendingSpin.winLines || [],
                scatterCount: pendingSpin.scatterCount || 0
              };
              
              handleWinReveal(winData);
            }, 3000);
          } else {
            setTimeout(() => {
              setShowCelebrationOverlay(false);
            }, 3000);
          }
        }
      }
    } catch (error) {
      console.error('Error checking for interrupted spins:', error);
      // Silently fail - this is just a recovery mechanism
    }
  };
  
  checkForInterruptedSpin();
}, []);

  // Add this function to your component
  const formatTimeRemaining = (targetTime) => {
    if (!targetTime) return "Available now";
    
    const now = new Date();
    const targetDate = new Date(targetTime);
    
    // If the target time is in the past, it's available now
    if (targetDate <= now) return "Available now";
    
    const diffMs = targetDate - now;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHrs > 0) {
      return `Available in ${diffHrs}h ${diffMins}m`;
    } else if (diffMins > 0) {
      return `Available in ${diffMins}m`;
    } else {
      return "Available in less than a minute";
    }
  };

  const checkDailyBonusStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get('/api/casino/bonus-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setIsDailyBonusClaimed(response.data.isAlreadyClaimed);
        if (response.data.isAlreadyClaimed && response.data.nextClaimTime) {
          setNextDailyBonusTime(new Date(response.data.nextClaimTime));
        }
      }
    } catch (error) {
      console.error('Failed to check daily bonus status:', error);
    }
  };

  // Add this new state variable for the countdown text
  const [bonusCountdownText, setBonusCountdownText] = useState("");

  // Add this effect to update the countdown
  useEffect(() => {
    // Skip if no next bonus time or if not claimed
    if (!isDailyBonusClaimed || !nextDailyBonusTime) return;
    
    // Initial update
    setBonusCountdownText(formatTimeRemaining(nextDailyBonusTime));
    
    // Set up interval to update every minute
    const intervalId = setInterval(() => {
      const newText = formatTimeRemaining(nextDailyBonusTime);
      setBonusCountdownText(newText);
      
      // If available now, refresh the bonus status
      if (newText === "Available now") {
        setIsDailyBonusClaimed(false);
        clearInterval(intervalId);
      }
    }, 60000); // Update every minute
    
    return () => clearInterval(intervalId);
  }, [isDailyBonusClaimed, nextDailyBonusTime]);
  
  // 4. Add this useEffect to fetch the pot amount when component loads
  useEffect(() => {
    fetchCommunityPotAmount();
  }, []);

  const fetchJackpotInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get('/api/casino/community-jackpot', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setJackpotInfo(response.data.jackpot);
        
        // Also fetch user's contributions (would be a separate endpoint in a real implementation)
        // For demo, we'll just set some mock data based on the total jackpot
        const mockTotal = Math.min(response.data.jackpot.totalAmount * 0.05, 120);
        const mockToday = Math.min(mockTotal * 0.2, 25);
        
        setUserJackpotContributions({
          total: mockTotal,
          today: mockToday,
          sharePercentage: (mockTotal / Math.max(1, response.data.jackpot.totalAmount)) * 100,
          estimatedReturn: mockTotal * 1.2, // Estimated 120% return on contribution
          lastDistribution: response.data.jackpot.totalAmount * 0.8, // 80% of total
          yourLastShare: mockTotal * 0.8 // 80% of user's contribution
        });
      }
    } catch (error) {
      console.error('Failed to fetch jackpot information:', error);
    }
  };

  // Function to stop spin sound
  const stopSpinSound = () => {
    if (soundEnabled && spinSoundRef.current) {
      // If the sound is already playing
      if (!spinSoundRef.current.paused) {
        // Get the current volume
        const originalVolume = spinSoundRef.current.volume;
        const fadeSteps = 5; // OPTIMIZATION: 5 steps instead of 10
        const fadeInterval = 30; // OPTIMIZATION: 30ms instead of 50ms
        const volumeStep = originalVolume / fadeSteps;
        
        // Create a fade-out effect
        let currentStep = 0;
        const fadeTimer = setInterval(() => {
          currentStep++;
          if (currentStep >= fadeSteps || spinSoundRef.current.volume <= 0.02) {
            // Clear the interval and fully stop the sound
            clearInterval(fadeTimer);
            spinSoundRef.current.pause();
            spinSoundRef.current.currentTime = 0;
            // Reset volume for next play
            spinSoundRef.current.volume = originalVolume;
          } else {
            // Reduce the volume gradually
            spinSoundRef.current.volume = Math.max(0, originalVolume - (volumeStep * currentStep));
          }
        }, fadeInterval);
      } else {
        // If sound is already paused, just reset it
        spinSoundRef.current.currentTime = 0;
      }
    }
  };
  
  // Play a sound if sounds are enabled
  const playSound = (soundKey) => {
    if (!soundEnabled) return;
    
    // For win sounds, fade-out spin sound first rather than stopping abruptly
    if (soundKey.includes('WIN')) {
      stopSpinSound();
    }
    
    // Get the correct audio element
    const audioElement = audioRefs.current[soundKey];
    if (!audioElement) return;
    
    // For SPIN_START, use the direct reference and don't clone
    if (soundKey === 'SPIN_START') {
      // If it's already playing, don't restart - just maintain or increase volume
      if (!spinSoundRef.current.paused) {
        // Ensure volume is at desired level (might have been fading)
        spinSoundRef.current.volume = 0.5;
        return;
      }
      
      // Start at a lower volume and fade in
      spinSoundRef.current.volume = 0.1;
      spinSoundRef.current.play().catch(e => console.log("Spin audio play prevented:", e));
      
      // Fade in the spin sound
      let fadeInStep = 0;
      const fadeInInterval = setInterval(() => {
        fadeInStep++;
        if (fadeInStep >= 8) {
          clearInterval(fadeInInterval);
        } else {
          spinSoundRef.current.volume = Math.min(0.5, 0.1 + (fadeInStep * 0.05));
        }
      }, 60);
      
      return;
    }
    
    // Clone the audio to allow overlapping sounds for other effects
    const sound = audioElement.cloneNode();
    
    // Set appropriate volume based on sound type
    if (soundKey.includes('WIN')) {
      // Fade in win sounds
      sound.volume = 0.1;
      sound.play().catch(e => console.log("Audio play prevented:", e));
      
      // Gradually increase volume for dramatic effect
      let fadeInStep = 0;
      const fadeInInterval = setInterval(() => {
        fadeInStep++;
        if (fadeInStep >= 8 || sound.ended || sound.paused) {
          clearInterval(fadeInInterval);
        } else {
          sound.volume = Math.min(0.7, 0.1 + (fadeInStep * 0.075));
        }
      }, 70);
    } else {
      // For other sounds
      sound.volume = soundKey === 'ERROR' ? 0.6 : 0.5;
      sound.play().catch(e => console.log("Audio play prevented:", e));
    }
  };
  
  // Add animations and styles for the slot machine
  useEffect(() => {
    // Cabinet light animations
    const style = document.createElement('style');
    style.textContent = `
      .win-highlight {
        animation: win-pulse 0.5s infinite;
        z-index: 30;
      }

      .win-highlight-1 { animation-delay: 0ms; }
      .win-highlight-2 { animation-delay: 100ms; }
      .win-highlight-3 { animation-delay: 200ms; }
      .win-highlight-4 { animation-delay: 300ms; }
      .win-highlight-5 { animation-delay: 400ms; }

      @keyframes win-pulse {
        0% { transform: scale(1); filter: brightness(1); }
        50% { transform: scale(1.1); filter: brightness(1.3); }
        100% { transform: scale(1); filter: brightness(1); }
      }
      @keyframes cabinetLight {
        0% { opacity: 0.3; }
        50% { opacity: 1; }
        100% { opacity: 0.3; }
      }
      
      @keyframes marqueeScroll {
        0% { transform: translateX(100%); }
        100% { transform: translateX(-100%); }
      }
      
      @keyframes scanline {
        0% { background-position: 0 0; }
        100% { background-position: 0 30px; }
      }

      .win-sequence {
        position: relative;
        z-index: 40;
        animation: win-pulse 0.8s infinite;
      }

      @keyframes win-pulse {
        0% { transform: scale(1); filter: brightness(1); }
        50% { transform: scale(1.1); filter: brightness(1.3); }
        100% { transform: scale(1); filter: brightness(1); }
      }

      .animate-win-pulse {
        animation: win-pulse 0.8s infinite;
      }

      @keyframes win-pulse {
        0% { transform: scale(1); filter: brightness(1); }
        50% { transform: scale(1.1); filter: brightness(1.3); }
        100% { transform: scale(1); filter: brightness(1); }
      }
      
      @keyframes confetti {
        0% { transform: translateY(-100%) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
      
      @keyframes cabinet-glow {
        0% { box-shadow: 0 0 5px #f0c, 0 0 10px #f0c; }
        50% { box-shadow: 0 0 20px #f0c, 0 0 30px #f0c; }
        100% { box-shadow: 0 0 5px #f0c, 0 0 10px #f0c; }
      }
      
      @keyframes jackpot-pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      
      .animate-confetti {
        animation: confetti 3s ease-in-out forwards;
      }
      
      .animate-cabinet-light {
        animation: cabinetLight 2s infinite;
      }
      
      .animate-marquee {
        animation: marqueeScroll 20s linear infinite;
      }
      
      .animate-scanline {
        animation: scanline 1s linear infinite;
      }
      
      .animate-cabinet-glow {
        animation: cabinet-glow 3s infinite;
      }
      
      .animate-jackpot-pulse {
        animation: jackpot-pulse 2s infinite;
      }
      
      .slot-scanline {
        background-image: linear-gradient(transparent 50%, rgba(0, 0, 0, 0.1) 50%);
        background-size: 100% 4px;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Marquee animation effect
  useEffect(() => {
    if (marqueeRef.current) {
      marqueeRef.current.style.animationDuration = `${casinoMessages.length * 5}s`;
    }
  }, [casinoMessages.length]);
  
  // Free spins effect - automatically triggers free spins
  useEffect(() => {
    let freeSpinTimer;
    
    if (freeSpins > 0 && !spinning && !freeSpinInProgress) {
      setFreeSpinInProgress(true);
      
      // Set the free spin message
      // setCelebrationType('freespin');
      // setCelebrationData({ message: `FREE SPIN ACTIVE! (${freeSpins} remaining)` });
      // setShowCelebrationOverlay(true);
      
      // Play free spin sound
      playSound('FREE_SPINS');
      
      // Flash cabinet lights
      if (machineRef.current) {
        machineRef.current.classList.add('animate-cabinet-glow');
        setTimeout(() => {
          machineRef.current.classList.remove('animate-cabinet-glow');
        }, 3000);
      }
      
      freeSpinTimer = setTimeout(() => {
        setShowCelebrationOverlay(false);
        handleSpin(true);
      }, 2000);
    }
    
    return () => {
      if (freeSpinTimer) clearTimeout(freeSpinTimer);
    };
  }, [freeSpins, spinning, freeSpinInProgress]);

  // Handle reel stop sequence for realistic slot experience
  useEffect(() => {
    if (reelStopSequence.length > 0 && spinning) {
      const currentReel = reelStopSequence[0];
      
      // Play reel stop sound
      playSound('REEL_STOP');
      
      // Visual effect for reel stopping
      if (reelsRef.current) {
        const reelElements = reelsRef.current.children;
        if (reelElements[currentReel]) {
          reelElements[currentReel].classList.add('border-yellow-400', 'shadow-lg', 'shadow-yellow-400/50');
          setTimeout(() => {
            reelElements[currentReel].classList.remove('border-yellow-400', 'shadow-lg', 'shadow-yellow-400/50');
          }, 200);
        }
      }
      
      // Remove the current reel from the sequence
      const newSequence = [...reelStopSequence];
      newSequence.shift();
      
      // Check if we're done with all reels
      if (newSequence.length === 0) {
        // Handle any pending win after all reels have stopped
        if (pendingWin !== null) {
          handleWinReveal(pendingWin);
          setPendingWin(null);
        }
      }
      
      // OPTIMIZATION: Set the updated sequence after a faster delay
      setTimeout(() => {
        setReelStopSequence(newSequence);
      }, 150); // OPTIMIZATION: Reduced from 300ms to 150ms per reel
    }
  }, [reelStopSequence, spinning, pendingWin]);

  // Add this near the top of your component
const extractUserIdFromToken = (token) => {
  try {
    // Try to parse the JWT to get user ID
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    return JSON.parse(jsonPayload).userId;
  } catch (e) {
    // If JWT parsing fails, try to get from localStorage
    return localStorage.getItem('userId');
  }
};

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
  
        checkDailyBonusStatus();
        
        // Get JWT token
        const token = localStorage.getItem('token');
        if (!token) {
          setError("Authentication required. Please log in.");
          setLoading(false);
          return;
        }
        
        // Get user ID from JWT or local storage
        let userId = extractUserIdFromToken(token); // Create this helper function
        
        if (!userId) {
          setError("User ID not found. Please log in again.");
          setLoading(false);
          return;
        }
        
        // Fetch user data from API
        const response = await axios.get(`/api/users/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Process successful response
        const userData = response.data;
        setUser(userData);
        setBalance(userData.chips || 0);

        if (userData) {
          // Get the timestamp of when the free spins were reset (update this to your actual reset time)
          const resetTimestamp = Date.now(); // Current timestamp - update this with your reset timestamp
          // For example: const resetTimestamp = 1747411200000; // May 17, 2025
          
          // Get last sync time from localStorage
          const lastSync = parseInt(localStorage.getItem('lastFreeSpinsSync') || '0', 10);
          
          // If this is first login after the reset, force server values (which should be 0)
          if (lastSync < resetTimestamp) {
            console.log('First login after free spins reset - using server value');
            
            // Trust the server value (which should be 0 after your reset)
            setFreeSpins(userData.freeSpins || 0);
            localStorage.setItem('freeSpins', (userData.freeSpins || 0).toString());
            
            // Update the last sync timestamp to current time
            localStorage.setItem('lastFreeSpinsSync', Date.now().toString());
          } else {
            // For future logins, use original synchronization logic
            const localFreeSpins = parseInt(localStorage.getItem('freeSpins') || '0', 10);
            
            // If we have local free spins but server shows zero, update the server
            if (localFreeSpins > 0 && (!userData.freeSpins || userData.freeSpins === 0)) {
              console.log(`Syncing ${localFreeSpins} free spins to server`);
              try {
                // Update server with localStorage value
                await saveFreeSpinsToServer(localFreeSpins);
                
                // Update local state
                setFreeSpins(localFreeSpins);
              } catch (syncError) {
                console.error("Failed to sync free spins to server:", syncError);
                // Fall back to server value for consistency
                setFreeSpins(userData.freeSpins || 0);
                localStorage.setItem('freeSpins', (userData.freeSpins || 0).toString());
              }
            } else {
              // Use the higher value between server and local storage
              const finalCount = Math.max(localFreeSpins, userData.freeSpins || 0);
              setFreeSpins(finalCount);
              localStorage.setItem('freeSpins', finalCount.toString());
            }
          }
        }
        
        // Set VIP level based on user data
        if (userData.totalCasinoPlays > 1000) {
          setVipLevel(5); // Royal
        } else if (userData.totalCasinoPlays > 500) {
          setVipLevel(4); // Diamond
        } else if (userData.totalCasinoPlays > 250) {
          setVipLevel(3); // Platinum
        } else if (userData.totalCasinoPlays > 100) {
          setVipLevel(2); // Gold
        } else if (userData.totalCasinoPlays > 50) {
          setVipLevel(1); // Silver
        } else {
          setVipLevel(0); // Bronze
        }
        
      } catch (err) {
        console.error('Failed to load user data:', err);
        // Your existing fallback logic
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
    
    // Add this periodic balance sync
    const balanceSyncInterval = setInterval(async () => {
      try {
        // Only sync if not currently spinning
        if (spinning) return;
        
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const userId = extractUserIdFromToken(token);
        if (!userId) return;
        
        const response = await axios.get(`/api/users/${userId}/balance-check`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success && response.data.chips !== undefined) {
          // If there's a mismatch beyond a small threshold, update
          const diff = Math.abs(balance - response.data.chips);
          if (diff > 0.01) {
            console.log(`Balance sync: Local ${balance} vs Server ${response.data.chips}`);
            setBalance(response.data.chips);
          }
        }
      } catch (error) {
        // Silent fail - don't disrupt the user experience
        console.error('Balance sync error:', error);
      }
    }, 30000); // Every 30 seconds
    
    // Clean up interval on component unmount
    return () => clearInterval(balanceSyncInterval);
  }, []);

  

  // Format currency
  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  }
  
  // Count scatters in a grid
  const countScatters = (grid) => {
    let count = 0;
    grid.forEach(row => {
      row.forEach(symbol => {
        if (symbol === "scatter") {
          count++;
        }
      });
    });
    return count;
  };
  
  // Adjust bet amount
  const adjustBet = (delta) => {
    if (spinning) return;
    
    // Play button click sound
    playSound('BUTTON_CLICK');
    
    const newBet = Math.max(0.20, Math.min(20, betAmount + delta));
    setBetAmount(parseFloat(newBet.toFixed(2)));
  };
  
  // Generate a sequence of column indices for reels to stop in order
  const generateReelStopSequence = () => {
    // Instead of left-to-right sequential stops, introduce a slightly more
    // realistic pattern that still feels fast
    return [0, 3, 1, 4, 2]; // This stops columns 0, 3, 1, 4, 2 in that order
    // The effect feels more dynamic and less predictable while finishing in the same time
  };
  
  // Handle Win Reveal - called after all reels have stopped
// The key issue is in the handleWinReveal function
// Here's the updated version that stops the spin sound for both win and no-win cases

const handleWinReveal = (winData) => {
  setShowLostFeedback(false);
  const { amount, lines, scatterCount } = winData;
  
  // Function to highlight win sequence positions - remains the same
  const highlightWinSequence = (winLine) => {
    if (!winLine || !winLine.positions || winLine.positions.length < 3) return;
    
    // Clear any existing highlights first
    document.querySelectorAll('.win-sequence-indicator').forEach(el => {
      el.remove();
    });
    
    // Find all cells that are part of this win
    winLine.positions.forEach((pos, index) => {
      setTimeout(() => {
        // Find the cell element - adapt this selector to match your actual DOM structure
        const cellSelector = `.symbol-cell-${pos.row}-${pos.col}`;
        const cellElement = document.querySelector(cellSelector);
        
        if (cellElement) {
          // Add a sequence number indicator
          const indicator = document.createElement('div');
          indicator.className = 'win-sequence-indicator absolute top-1 left-1 w-5 h-5 bg-yellow-500 rounded-full text-black text-xs font-bold flex items-center justify-center z-50';
          indicator.textContent = index + 1;
          cellElement.appendChild(indicator);
          
          // Add a pulse animation class to the cell
          cellElement.classList.add('animate-win-pulse');
          
          // Play a subtle sound if enabled
          if (soundEnabled) {
            playSound('BUTTON_CLICK');
          }
        }
      }, index * 100); // OPTIMIZATION: 100ms instead of 150ms
    });
  };
  
  // OPTIMIZATION: Reduce initial delay from 300ms to 100ms
  setTimeout(() => {
    setWinLines(lines);
    
    // Show paylines if there are winning lines
    if (lines && lines.length > 0) {
      // Get the regular payline wins (not scatter)
      const regularWins = lines.filter(line => line.line !== -1);
      
      if (regularWins.length > 0) {
        // Get the payline IDs from the winning lines
        const winningPaylineIds = regularWins.map(line => line.line);
        
        // First show all winning paylines
        setVisiblePaylines(winningPaylineIds);
        
        // OPTIMIZATION: For multiple wins, only highlight the best one to save time
        if (regularWins.length > 0) {
          // Find the best win (highest value)
          const bestWin = regularWins.reduce((best, current) => 
            current.win > best.win ? current : best, regularWins[0]);
          
          // OPTIMIZATION: Reduce the delay before showing paylines
          setTimeout(() => {
            // Focus on just this one payline
            setVisiblePaylines([bestWin.line]);
            // Highlight the win sequence
            highlightWinSequence(bestWin);
            
            // OPTIMIZATION: Show all paylines again after a shorter delay
            setTimeout(() => {
              setVisiblePaylines(winningPaylineIds);
              
              // OPTIMIZATION: Clear highlights faster (1000ms instead of 2000ms)
              setTimeout(() => {
                setVisiblePaylines([]);
                document.querySelectorAll('.win-sequence-indicator').forEach(el => {
                  el.remove();
                });
                document.querySelectorAll('.animate-win-pulse').forEach(el => {
                  el.classList.remove('animate-win-pulse');
                });
              }, 1000);
            }, 750); // OPTIMIZATION: 750ms instead of waiting for all animations
          }, 400); // OPTIMIZATION: 400ms delay instead of 800ms
        } else {
          // If no sequence highlighting, just show paylines for a shorter time
          setTimeout(() => {
            setVisiblePaylines([]);
          }, 1500); // OPTIMIZATION: 1.5s instead of 4s
        }
      }
    }
    
    // Handle win notification with better sequencing
    if (amount > 0) {
      const roundedWin = amount;
      setWinAmount(roundedWin);
      
      // Add the win amount to balance
      // setBalance(prev => prev + roundedWin);

      if (user && user._id) {
        const token = localStorage.getItem('token');
        axios.get(`/api/users/${user._id}/balance-check`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).then(response => {
          if (response.data.success && response.data.chips !== undefined) {
            setBalance(response.data.chips);
          }
        }).catch(err => {
          console.error("Failed to fetch updated balance after win:", err);
        });
      }

      setSessionWins(prev => prev + roundedWin);
      
      // Shake the machine for big wins
      if (roundedWin >= betAmount * 5 && machineRef.current) {
        machineRef.current.classList.add('animate-pulse');
        setTimeout(() => {
          machineRef.current.classList.remove('animate-pulse');
        }, 1000);
      }
      
      // Play appropriate win sound (which will fade out the spin sound)
      if (roundedWin >= betAmount * 20) {
        playSound('WIN_MEGA');
        playSound('COINS_BURST');
      } else if (roundedWin >= betAmount * 10) {
        playSound('WIN_BIG');
      } else if (roundedWin >= betAmount * 5) {
        playSound('WIN_MEDIUM');
      } else {
        playSound('WIN_SMALL');
      }
      
      // Show win celebration
      setCelebrationType('win');
      
      // Include free spin multiplier info if in free spin mode
      const celebrationInfo = { amount: roundedWin };
      if (freeSpins > 0) {
        celebrationInfo.freeSpinMultiplier = 2;
      }
      setCelebrationData(celebrationInfo);
      setShowCelebrationOverlay(true);
  
      // OPTIMIZATION: Reduce display times for all win sizes
      let displayTime = 1500; // OPTIMIZATION: 1.5s default (down from 2.5s)
      
      if (roundedWin >= betAmount * 20) {
        displayTime = 2500; // OPTIMIZATION: 2.5s for mega wins (down from 5s)
      } else if (roundedWin >= betAmount * 10) {
        displayTime = 2000; // OPTIMIZATION: 2s for super wins (down from 4s)
      } else if (roundedWin >= betAmount * 5) {
        displayTime = 1750; // OPTIMIZATION: 1.75s for big wins (down from 3.5s)
      }
      
      // Hide the celebration after display time
      setTimeout(() => {
        setShowCelebrationOverlay(false);
        
        // Check for free spins AFTER win celebration
        if (scatterCount >= 3) {
          handleFreeSpinAward(scatterCount, true); // true = there was a win
        }
      }, displayTime);
    } else {
      // For NO WIN, fade out the spin sound and show a message
      stopSpinSound();
      
      // Optional: play a subtle "no win" sound
      // playSound('NO_WIN'); // You would need to add this to AUDIO_PATHS
      
      setCelebrationType('nowin');
      setShowCelebrationOverlay(true);
      
      // OPTIMIZATION: Clear celebration faster (1000ms instead of 2000ms)
      setTimeout(() => {
        setShowCelebrationOverlay(false);
        
        // Handle scatter-triggered free spins AFTER message
        if (scatterCount >= 3) {
          handleFreeSpinAward(scatterCount, false); // false = no win
        }
      }, 2000); // OPTIMIZATION: 1s instead of 2s
    }
  }, 100); // OPTIMIZATION: Initial delay reduced from 300ms to 100ms
};
  
const handleFreeSpinAward = (scatterCount, hasOtherWin) => {
  if (scatterCount < 3) return;
  
  const newFreeSpins = scatterCount;
  
  // OPTIMIZATION: Reduce the initial delay 
  const initialDelay = hasOtherWin ? 500 : 250; // OPTIMIZATION: 500ms/250ms instead of 1000ms/500ms
  
  // Schedule the free spins awarded message
  setTimeout(() => {
    // Play free spins sound
    playSound('FREE_SPINS');
    
    // Enhance cabinet lighting for free spins
    if (machineRef.current) {
      machineRef.current.classList.add('animate-cabinet-glow');
    }
    
    // Show the free spins awarded message with scatter count
    setCelebrationType('freespin');
    setCelebrationData({ 
      message: `${scatterCount}x ${
        // Use an image tag for the scatter icon
        '<img src="' + symbolStyles["scatter"].imageSrc + '" alt="Scatter" class="inline-block h-4 w-4" />'
      } = ${newFreeSpins} FREE SPINS AWARDED!` 
    });
    setShowCelebrationOverlay(true);
    
    // OPTIMIZATION: Show message for 1.5s instead of 3s
    setTimeout(() => {
      setShowCelebrationOverlay(false);
      
      // Now actually update the free spins count
      setFreeSpins(prev => {
        const updatedFreeSpins = prev + newFreeSpins;
        
        // Save to server if user is logged in
        if (user && user._id) {
          saveFreeSpinsToServer(updatedFreeSpins);
        }
        
        return updatedFreeSpins;
      });
      
      // IMPORTANT: Store the current bet amount as the triggering bet
      setFreeSpinBetAmount(betAmount);
      localStorage.setItem('freeSpinBetAmount', betAmount.toString());
      
      // Log the saved triggering bet amount
      console.log(`Saved triggering bet amount: ${betAmount} for ${newFreeSpins} free spins`);
      
      // Remove cabinet glow
      if (machineRef.current) {
        machineRef.current.classList.remove('animate-cabinet-glow');
      }
    }, 1500); // OPTIMIZATION: 1.5s instead of 3s
  }, initialDelay);
};
  
const handleSpin = async (isFreeSpin = false) => {
  if (spinning) return;

  // Store current balance for potential rollback
  const currentBalance = balance;

  const effectiveCap = sessionWinCap * betAmount;
  if (sessionWins > effectiveCap) {
    handleError("SESSION WIN LIMIT REACHED!");
    playSound('ERROR');
    return;
  }
  
  console.log("Starting spin, isFreeSpin:", isFreeSpin);

  const effectiveBetAmount = isFreeSpin && freeSpinBetAmount ? freeSpinBetAmount : betAmount;
  console.log(`Using bet amount: ${effectiveBetAmount} (Free spin: ${isFreeSpin})`);
  
  // Play spin start sound
  playSound('SPIN_START');
  
  // Set last win to current win amount before resetting
  setLastWin(winAmount);
  
  // Clear all notifications and win states
  setWinLines([]);
  setWinAmount(0);
  setShowCelebrationOverlay(false);
  setVisiblePaylines([]);
  
  // Check balance for normal spins
  if (!isFreeSpin && balance < betAmount) {
    handleError("INSUFFICIENT BALANCE!");
    playSound('ERROR');
    stopSpinSound();
    return;
  }
  
  // Start spinning animation
  setSpinning(true);
  
  // Declare the interval variable here so it's accessible in both try and catch blocks
  let spinInterval;
  
  try {
    // Optimistically update the balance for UI responsiveness
    // This will be corrected with the server's value if there's an error
    if (!isFreeSpin) {
      setBalance(prev => prev - effectiveBetAmount);
    }
    
    // OPTIMIZATION 1: Reduce animation phases from 30 to 12 and interval from 100ms to 50ms
    // This cuts animation time by more than half (from 3s to ~0.6s)
    let spinPhase = 0;
    spinInterval = setInterval(() => {
      spinPhase++;
      
      // Create random animation grid during spin
      const animationGrid = Array(rows).fill().map(() => 
        Array(cols).fill().map(() => {
          // Simple random selection for animation purposes only
          const symbolNames = Object.keys(symbolStyles);
          return symbolNames[Math.floor(Math.random() * symbolNames.length)];
        })
      );
      setGrid(animationGrid);
      
      // OPTIMIZATION: Stop after 12 phases instead of 30
      if (spinPhase >= 12) {
        clearInterval(spinInterval);
      }
    }, 50); // OPTIMIZATION: 50ms instead of 100ms
    
    // Request quantum spin from backend
    const token = localStorage.getItem('token');
    
    const response = await axios.post('/api/casino/quantum/spin', {
      betAmount: betAmount,
      isFreeSpin,
      streakBreaker: streakBreakerActive
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Clear animation interval once we get a response
    clearInterval(spinInterval);
    
    if (response.data.success) {
      // Get the result directly
      const result = response.data;
      
      // Generate reel stop sequence
      const reelSequence = generateReelStopSequence();
      setReelStopSequence(reelSequence);
      
      // Update the grid with the quantum-generated result
      setGrid(result.result.grid);
      
      // Use the server-confirmed balance - this is critical for accuracy
      if (result.remainingChips !== undefined) {
        setBalance(result.remainingChips);
      }
      
      // Set pending win to be processed after reels stop
      setPendingWin({
        amount: result.payout,
        lines: result.result.winLines,
        scatterCount: countScatters(result.result.grid)
      });

      if (response.data.communityPotContribution > 0) {
        setLastLostAmount(response.data.communityPotContribution);
        setCommunityPotAmount(response.data.currentPotTotal);
        
        // Show lost feedback message after a short delay
        setTimeout(() => {
          setShowLostFeedback(true);
          setTimeout(() => setShowLostFeedback(false), 2000); // OPTIMIZATION: 2s instead of 3s
        }, 500);
      }
      
      // Free spin handling
      if (isFreeSpin) {
        // Apply multiplier to any wins during free spins (before updating state)
        if (result.payout > 0) {
          // Apply 1.2x multiplier to all wins during free spins
          const freeSpinMultiplier = 1.2;
          result.payout *= freeSpinMultiplier;
          
          // Also multiply each individual win line amount
          if (result.result && result.result.winLines) {
            result.result.winLines.forEach(line => {
              line.win *= freeSpinMultiplier;
            });
          }
          
          // Add visual indicator that win was multiplied
          setCelebrationData(prev => ({
            ...prev,
            freeSpinMultiplier: freeSpinMultiplier
          }));
          
          console.log(`Applied ${freeSpinMultiplier}x multiplier to free spin win: ${result.payout}`);
        }
      
        // Then update the free spins count as before
        setFreeSpins(prev => {
          const newCount = prev - 1;
          localStorage.setItem('freeSpins', newCount.toString());
          
          // Also update on server if user is logged in
          if (user && user._id) {
            saveFreeSpinsToServer(newCount);
          }
          
          // If this was the last free spin, clear the triggering bet amount
          if (newCount === 0) {
            setFreeSpinBetAmount(null);
            localStorage.removeItem('freeSpinBetAmount');
            console.log("All free spins used, clearing triggering bet amount");
          }
          
          return newCount;
        });
        setFreeSpinInProgress(false);
      }
      
      // Set fairness proof
      setFairnessProof({
        requestId: result.requestId,
        timestamp: new Date().toISOString(),
        betAmount: effectiveBetAmount, // Use the effective bet amount here
        payout: result.payout,
        grid: result.result.grid,
        method: 'quantum-rng',
        quantumSource: result.quantumSource,
        verificationHash: result.verificationHash,
        verificationUrl: result.verificationUrl
      });
      
      // OPTIMIZATION: Set spinning false faster
      // Change from reelSequence.length * 300 + 200 to reelSequence.length * 150 + 100
      setTimeout(() => {
        setSpinning(false);
      }, reelSequence.length * 150 + 100); // OPTIMIZATION: Cut time in half (from 1.7s to 0.85s)

      // Track consecutive wins for streak breaker
      if (result.payout > 0) {
        // Win - increment counter
        setConsecutiveWins(prev => prev + 1);
        
        // If we've won 3+ times in a row, activate streak breaker for next spin
        if (consecutiveWins >= 2) {
          setStreakBreakerActive(true);
          console.log("Streak breaker activated for next spin");
        }
      } else {
        // Reset consecutive wins on a loss
        setConsecutiveWins(0);
        setStreakBreakerActive(false);
      }
    } else {
      // Handle error - roll back the optimistic UI update
      if (!isFreeSpin) {
        setBalance(currentBalance);
      }
      
      clearInterval(spinInterval);
      setSpinning(false);
      setFreeSpinInProgress(false);
      
      // Show the specific error message
      handleError(response.data.error || "Failed to request spin");
      playSound('ERROR');
    }
  } catch (error) {
    console.error('Error requesting spin:', error);
    
    // Roll back the optimistic UI update on any error
    if (!isFreeSpin) {
      setBalance(currentBalance);
    }
    
    // Now we can safely clear the interval since it's defined in a higher scope
    if (spinInterval) {
      clearInterval(spinInterval);
    }
    
    // Check for the specific "No free spins available" error
    if (error.response && 
        error.response.data && 
        error.response.data.error === 'No free spins available') {
      
      console.log("Server reports no free spins available, synchronizing...");
      
      // Update local storage and state to match server
      setFreeSpins(0);
      localStorage.setItem('freeSpins', '0');
      
      // Show a more specific error message
      handleError("NO FREE SPINS AVAILABLE!");
    } else {
      // For other errors, show the generic connection error
      handleError("CONNECTION ERROR!");
    }
    
    playSound('ERROR');
    setSpinning(false);
    setFreeSpinInProgress(false);
    stopSpinSound();
  }
};

  // Check if position is part of a winning line
  const isWinningPosition = (rowIdx, colIdx) => {
    if (!winLines || winLines.length === 0) return false;
    
    return winLines.some(line => 
      line.positions && line.positions.some(pos => {
        // Check both possible position formats
        if (typeof pos === 'object' && pos !== null) {
          if ('row' in pos && 'col' in pos) {
            return pos.row === rowIdx && pos.col === colIdx;
          } else if (Array.isArray(pos)) {
            return pos[0] === rowIdx && pos[1] === colIdx;
          }
        }
        return false;
      })
    );
  };

  const highlightWinSequence = (winLine, delay = 0) => {
    if (!winLine || !winLine.positions || winLine.positions.length < 3) return;
    
    // Clear any existing highlights
    document.querySelectorAll('.win-highlight').forEach(el => {
      el.classList.remove('win-highlight', 'win-highlight-1', 'win-highlight-2', 'win-highlight-3', 'win-highlight-4', 'win-highlight-5');
    });
    
    // Highlight each position in sequence with increasing delays
    winLine.positions.forEach((pos, index) => {
      setTimeout(() => {
        const rowIdx = pos.row;
        const colIdx = pos.col;
        const cellElement = document.querySelector(`.reel-cell-${rowIdx}-${colIdx}`);
        
        if (cellElement) {
          // Add highlight with sequence number
          cellElement.classList.add('win-highlight', `win-highlight-${index + 1}`);
          
          // Optionally play a sound for each highlight
          playSound('WIN_STEP');
        }
      }, delay + (index * 200)); // 200ms between each highlight
    });
  };

  // Check if position is part of a visible payline
  const isPaylinePosition = (rowIdx, colIdx) => {
    if (!visiblePaylines || visiblePaylines.length === 0) return false;
    
    return visiblePaylines.some(lineId => {
      const payline = paylines.find(p => p.id === lineId);
      if (!payline) return false;
      
      return payline.positions.some(pos => {
        // Handle both possible position formats
        if (Array.isArray(pos)) {
          return pos[0] === rowIdx && pos[1] === colIdx;
        } else if (pos.row !== undefined && pos.col !== undefined) {
          return pos.row === rowIdx && pos.col === colIdx;
        }
        return false;
      });
    });
  };
  
  // Get payline color for a position
  const getPaylineColor = (rowIdx, colIdx) => {
    for (const lineId of visiblePaylines) {
      const payline = paylines.find(p => p.id === lineId);
      if (!payline) continue;
      
      const isInPayline = payline.positions.some(([row, col]) => 
        row === rowIdx && col === colIdx
      );
      
      if (isInPayline) {
        return payline.color;
      }
    }
    return "";
  };

  // Set max bet
  const maxBet = () => {
    // Play button click sound
    playSound('BUTTON_CLICK');
    setBetAmount(20);
  };

  // Toggle sound
  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  // Display error message
  const handleError = (errorMessage) => {
    setCelebrationType('error');
    setCelebrationData({ message: errorMessage });
    setShowCelebrationOverlay(true);
    
    // Clear error after 3 seconds
    setTimeout(() => {
      setShowCelebrationOverlay(false);
    }, 3000);
  };

  // Calculate casino win rate
  const getWinRate = () => {
    if (!user) return '0%';
    
    // Use casinoWinRate if available directly
    if (user.casinoWinRate !== undefined) {
      return `${(user.casinoWinRate * 100).toFixed(1)}%`;
    }
    
    // Otherwise calculate from wins/totalPlays
    const wins = user.casinoWins || 0;
    const totalPlays = user.totalCasinoPlays || 0;
    
    if (totalPlays === 0) return '0%';
    return `${((wins / totalPlays) * 100).toFixed(1)}%`;
  };

  // Calculate account age
  const getAccountAge = () => {
    if (!user || !user.createdAt) return '0 days';
    const createdDate = new Date(user.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  // Get current VIP tier
  const getCurrentVipTier = () => {
    return vipTiers[vipLevel];
  };

  // Add this to reset session stats periodically
useEffect(() => {
  // Reset session stats every hour
  const resetInterval = setInterval(() => {
    setSessionWins(0);
    setSessionStartTime(Date.now());
    console.log("Session stats reset due to time interval");
  }, 60 * 60 * 1000); // 1 hour
  
  return () => clearInterval(resetInterval);
}, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-mono flex items-center justify-center">
        <div className="w-full max-w-lg mx-auto shadow-lg p-6 text-center relative">
          {/* Metallic frame styling */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-700 via-gray-800 to-gray-700 rounded-xl border-2 border-gray-600 shadow-xl"></div>
          
          <div className="relative flex flex-col items-center p-8">
            <div className="w-24 h-24 border-4 border-violet-500 rounded-full relative flex items-center justify-center">
              <div className="w-16 h-16 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 border-t-4 border-violet-500 rounded-full animate-spin"></div>
            </div>
            <div className="mt-6 font-mono text-lg">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 font-mono animate-pulse">
                SCATTER SLOTS
              </span>
              <div className="mt-2 text-sm">Loading Vegas experience...</div>
            </div>
            
            <div className="mt-6 grid grid-cols-5 gap-1">
              {Array(5).fill().map((_, i) => (
                <div 
                  key={i}
                  className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center"
                >
                  <div 
                    className={`w-6 h-6 rounded-full ${
                      i % 5 === 0 ? "bg-red-500" : 
                      i % 5 === 1 ? "bg-yellow-500" : 
                      i % 5 === 2 ? "bg-green-500" : 
                      i % 5 === 3 ? "bg-blue-500" : 
                      "bg-purple-500"
                    } animate-pulse`}
                    style={{ animationDelay: `${i * 0.15}s` }}
                  ></div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 text-xs text-gray-400">
              PLAY RESPONSIBLY • 18+ ONLY
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[600px] bg-gradient-to-br from-gray-900 to-black -mt-16 pt-4 pb-12 px-4 text-white font-mono relative overflow-hidden">
      {/* Background casino lighting effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-pink-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
        
        {/* Vegas-style light beam effects */}
        <div className="absolute top-0 left-1/2 h-full w-1 bg-gradient-to-b from-yellow-400/40 via-yellow-400/0 to-yellow-400/0 animate-pulse" style={{ animationDuration: '7s' }}></div>
        <div className="absolute top-0 left-1/4 h-full w-1 bg-gradient-to-b from-pink-400/30 via-pink-400/0 to-pink-400/0 animate-pulse" style={{ animationDuration: '10s' }}></div>
        <div className="absolute top-0 left-3/4 h-full w-1 bg-gradient-to-b from-blue-400/30 via-blue-400/0 to-blue-400/0 animate-pulse" style={{ animationDuration: '8s' }}></div>
      </div>
    
      {/* Main content container */}
      <div className="max-w-md sm:max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 pt-2 sm:pt-6 relative">
        {/* Navigation bar with back button and title */}
        <div className="flex items-center justify-between mb-2 sm:mb-4 relative">
          {/* Left button */}
          <div className="z-10">
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                window.history.back();
              }}
              className="bg-black/30 p-2 rounded-full border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-colors"
            >
              <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          </div>

          <div className="absolute z-50 left-1/2 transform -translate-x-1/2 -top-8 bg-red-600 text-white px-3 py-1 rounded-md text-xs font-bold animate-pulse shadow-lg shadow-red-600/30 border border-red-400">
  BETA
</div>
          
          {/* Centered title - positioned absolutely */}
          {/* <h1 className="absolute left-1/2 transform -translate-x-1/2 text-lg sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 drop-shadow-lg font-mono">
            SCATTER SLOTS
          </h1> */}
          
          {/* Right buttons */}
          <div className="flex items-center space-x-1 sm:space-x-2 z-10">
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                toggleSound();
              }}
              className="bg-black/30 p-1.5 sm:p-2 rounded-full border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-colors"
            >
              {soundEnabled ? (
                <SpeakerWaveIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <SpeakerXMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>
            
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setShowUserStats(true);
              }}
              className="bg-black/30 p-1.5 sm:p-2 rounded-full border border-gray-800 text-pink-400 hover:text-pink-300 hover:border-gray-700 transition-colors"
            >
              <UserIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setShowFairnessModal(true);
              }}
              className="bg-black/30 p-1.5 sm:p-2 rounded-full border border-cyan-900 text-cyan-400 hover:text-cyan-300 hover:border-cyan-700 transition-colors"
            >
              <LockClosedIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>

            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setShowCommunityPotModal(true);
              }}
              className="bg-black/30 p-1.5 sm:p-2 rounded-full border border-green-800 text-green-400 hover:text-green-300 hover:border-green-700 transition-colors"
            >
              <WalletIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
                
        {/* Casino certification banners */}
        <div className="flex justify-center gap-2 mb-3">
          <div className="bg-blue-900/30 px-2 py-1 rounded-md border border-blue-800 text-blue-400 text-xs flex items-center">
            <LockClosedIcon className="h-3 w-3 mr-1" />
            <span>CERTIFIED FAIR</span>
          </div>
          <div className="bg-green-900/30 px-2 py-1 rounded-md border border-green-800 text-green-400 text-xs flex items-center">
            <BeakerIcon className="h-3 w-3 mr-1" />
            <span>QUANTUM SECURED</span>
          </div>
        </div>

        {/* Main stats and information area */}
        <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 backdrop-blur-lg border border-gray-800 rounded-lg sm:rounded-xl p-2 sm:p-3 mb-2 sm:mb-4 relative min-h-[120px]">
          {/* Scrolling marquee ticker */}
          <div className="mb-2 bg-black/40 rounded-lg overflow-hidden p-1 border-b border-gray-800">
            <div ref={marqueeRef} className="text-xs whitespace-nowrap animate-marquee text-yellow-400">
              {casinoMessages.join(' • ')}
            </div>
          </div>
        
          {/* Celebration Overlay */}
          {showCelebrationOverlay ? (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              {celebrationType === 'win' && celebrationData && (
                <div className={`flex flex-col items-center justify-center p-3 rounded-xl
                  ${celebrationData.amount >= betAmount * 20 
                    ? "bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 animate-pulse shadow-xl shadow-yellow-500/50 border-2 border-yellow-300" 
                    : celebrationData.amount >= betAmount * 10 
                      ? "bg-gradient-to-r from-fuchsia-600 via-pink-600 to-fuchsia-600 animate-pulse shadow-xl shadow-pink-500/50 border-2 border-pink-300" 
                      : celebrationData.amount >= betAmount * 5
                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg shadow-indigo-500/40 border-2 border-indigo-300"
                        : "bg-gradient-to-r from-emerald-600 to-teal-600 border-2 border-emerald-300"
                  } w-full h-full`}>
                  <div className="text-lg sm:text-xl uppercase tracking-wider mb-1 text-white font-bold flex items-center">
                    {celebrationData.amount >= betAmount * 20 ? (
                      <>
                        <span className="animate-bounce inline-block mr-2">🏆</span>
                        MEGA WIN!!!
                        <span className="animate-bounce inline-block ml-2">🏆</span>
                      </>
                    ) : celebrationData.amount >= betAmount * 10 ? (
                      <>SUPER WIN!!</>
                    ) : celebrationData.amount >= betAmount * 5 ? (
                      <>BIG WIN!</>
                    ) : (
                      <>YOU WON!</>
                    )}
                  </div>
                  {/* <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
                    {formatCurrency(celebrationData.amount)}
                  </div> */}

                  <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
                    {celebrationData.amount} {celebrationData.amount === 1 ? "Chip" : "Chips"}
                  </div>
                  
                  {/* Win multiplier badge */}
                  <div className="bg-white text-sm rounded-full px-3 py-1 shadow-md text-pink-600 font-bold flex items-center">
                    <span className="mr-1">×</span>
                    {(celebrationData.amount / betAmount).toFixed(1)}
                  </div>
                </div>
              )}
              
              {celebrationType === 'nowin' && (
                <div className="bg-gray-800/80 text-gray-300 py-3 px-5 rounded-lg shadow-lg border border-gray-700 text-center">
                  <div className="text-xl font-bold uppercase tracking-wider mb-1">No Win</div>
                  <div className="text-sm text-gray-400 mb-2">Try again!</div>
                  <div className="text-xs text-emerald-400 flex items-center justify-center">
                    <WalletIcon className="h-3 w-3 mr-1 text-emerald-500" />
                    Added {lastLostAmount} {lastLostAmount === 1 ? "Chip" : "Chips"} to Community Pot
                  </div>
                </div>
              )}
              
              {celebrationType === 'freespin' && celebrationData && (
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white py-3 px-5 rounded-lg shadow-lg border-2 border-violet-400 text-center animate-bounce">
                  <div className="text-lg sm:text-xl font-bold mb-1 flex items-center justify-center">
                    <span className="inline-block mr-2">
                      {/* Show scatter image instead of emoji */}
                      <img src={symbolStyles["scatter"].imageSrc} alt="Scatter" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </span>
                    FREE SPINS!
                    <span className="inline-block ml-2">
                      <img src={symbolStyles["scatter"].imageSrc} alt="Scatter" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </span>
                  </div>
                  <div className="text-sm sm:text-base">
                    {celebrationData.showScatterIcon ? (
                      <span>
                        {celebrationData.message.split('SCATTER').map((part, index) => (
                          <Fragment key={index}>
                            {index > 0 && (
                              <img src={symbolStyles["scatter"].imageSrc} alt="Scatter" className="inline-block h-4 w-4 mx-1" />
                            )}
                            {part}
                          </Fragment>
                        ))}
                      </span>
                    ) : (
                      celebrationData.message
                    )}
                  </div>
                </div>
              )}

              
              {celebrationType === 'error' && celebrationData && (
                <div className="bg-red-700 text-white py-3 px-5 rounded-lg shadow-lg border border-red-500 text-center">
                  <div className="text-lg font-bold uppercase tracking-wider mb-1">Error</div>
                  <div className="text-sm">{celebrationData.message}</div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Normal Stats Content (Only visible when not showing celebration) */}
              <div className="flex items-center justify-center mb-2">
                <div className="rounded-full px-3 py-1 text-xs bg-gradient-to-r from-blue-900 to-indigo-900 border border-blue-700 text-blue-100 shadow-lg shadow-blue-900/30">
                  ⚛️ Quantum RNG Verified
                </div>
              </div>
              
              <p className="text-xs text-gray-400 text-center mb-2 sm:mb-3 px-1">
                Match 3+ symbols on a payline to win. Get 3+ scatter symbols for free spins!
              </p>
              
              {/* Gradient stats capsules */}
              <div className="flex justify-center gap-2 sm:gap-4 flex-wrap">
                {/* Balance/Chips */}
                <div className="flex items-center bg-gradient-to-r from-gray-800 to-gray-900 rounded-full py-1 sm:py-2 px-2 sm:px-4 border border-gray-700 shadow-lg shadow-gray-800/50">
                  <BanknotesIcon className="h-3 w-3 sm:h-5 sm:w-5 text-yellow-400 mr-1 sm:mr-2" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500 font-bold text-xs sm:text-sm">
                    {balance.toLocaleString()}
                  </span>
                </div>
                
                {/* Community Jackpot */}
                {/* <div className="flex items-center bg-gradient-to-r from-green-800 to-emerald-900 rounded-full py-1 sm:py-2 px-2 sm:px-4 border border-green-700 shadow-lg shadow-green-800/50">
                  <WalletIcon className="h-3 w-3 sm:h-5 sm:w-5 text-green-400 mr-1 sm:mr-2" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 font-bold text-xs sm:text-sm">
                    {formatCurrency(communityPotAmount)}
                  </span>
                </div> */}
                
                {/* Win Amount */}
                <div className="flex items-center bg-gradient-to-r from-gray-800 to-gray-900 rounded-full py-1 sm:py-2 px-2 sm:px-4 border border-gray-700 shadow-lg shadow-gray-800/50">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-5 sm:w-5 text-pink-400 mr-1 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-500 font-bold text-xs sm:text-sm">
                    {winAmount.toLocaleString()}
                  </span>
                </div>
                
                {/* Free Spins */}
                {freeSpins > 0 && (
                  <div className="flex items-center bg-gradient-to-r from-indigo-800 to-violet-900 rounded-full py-1 sm:py-2 px-2 sm:px-4 border border-indigo-700 shadow-lg shadow-indigo-900/50 animate-pulse">
                    <SpeakerWaveIcon className="h-3 w-3 sm:h-5 sm:w-5 text-indigo-300 mr-1 sm:mr-2" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-violet-300 font-bold text-xs sm:text-sm">
                      {freeSpins} Spins
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Win celebration overlay with confetti for big wins */}
        {showCelebrationOverlay && celebrationType === 'win' && celebrationData && celebrationData.amount >= betAmount * 5 && (
          <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
            {/* Confetti particles */}
            {Array(30).fill().map((_, i) => (
              <div key={i} 
                className={`absolute top-0 h-8 w-2 rounded-sm bg-gradient-to-b ${
                  i % 5 === 0 ? "from-pink-500 to-pink-400" :
                  i % 5 === 1 ? "from-blue-500 to-blue-400" :
                  i % 5 === 2 ? "from-yellow-500 to-yellow-400" :
                  i % 5 === 3 ? "from-green-500 to-green-400" :
                  "from-purple-500 to-purple-400"
                } animate-confetti opacity-90`}
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 1.5}s`,
                  animationDuration: `${3 + Math.random() * 2}s`,
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              ></div>
            ))}
            
            {/* Coins for mega wins */}
            {celebrationData.amount >= betAmount * 20 && Array(15).fill().map((_, i) => (
              <div key={`coin-${i}`} 
                className="absolute w-8 h-8 bg-yellow-400 rounded-full shadow-inner shadow-yellow-300 animate-confetti opacity-90 z-50"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 3}s`,
                  backgroundImage: "radial-gradient(circle, rgba(251,191,36,1) 0%, rgba(250,204,21,1) 40%, rgba(202,138,4,1) 100%)",
                  border: "2px solid #fbbf24"
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center text-yellow-800 font-bold text-xs">
                  $
                </div>
              </div>
            ))}
            
            {/* Light rays for special effects */}
            {celebrationData.amount >= betAmount * 10 && (
              <>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-yellow-500/30 via-orange-500/20 to-transparent rounded-full animate-pulse"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-tr from-pink-500/30 via-purple-500/20 to-transparent rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              </>
            )}
          </div>
        )}
      </div>

      {/* New promotion notification - Vegas style offer popup */}
      {newPromotion && (
        <div className="fixed top-4 right-4 z-50 w-64 bg-gradient-to-br from-purple-900 to-indigo-900 rounded-lg shadow-lg shadow-purple-900/50 border-2 border-purple-500 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-700 to-indigo-700 py-2 px-3 flex justify-between items-center">
            <div className="flex items-center">
              <GiftIcon className="h-4 w-4 text-yellow-300 mr-1.5" />
              <span className="text-white font-bold text-sm">Special Offer!</span>
            </div>
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setNewPromotion(false);
              }}
              className="text-gray-300 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-3">
            <div className="text-yellow-300 text-sm font-bold mb-1">
              🎰 WELCOME BONUS
            </div>
            <div className="text-white text-xs mb-2">
              Deposit now and get a 200% matching bonus up to $500!
            </div>
            <button 
              onClick={() => {
                playSound('BUTTON_CLICK');
                setShowPromotions(true);
                setNewPromotion(false);
              }}
              className="w-full py-1.5 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 rounded text-xs font-bold text-white"
            >
              CLAIM NOW
            </button>
          </div>
        </div>
      )}

{/* Game container - Enhanced cabinet styling */}
<div className="max-w-md mx-auto px-2 sm:px-4 mb-8 sm:mb-12">
        {/* Slot machine cabinet with metallic styling */}
        <div 
          ref={machineRef}
          className="bg-gradient-to-b from-gray-800 via-gray-850 to-gray-900 rounded-xl shadow-2xl overflow-hidden border-4 border-gray-700 relative"
        >
          {/* Casino cabinet decoration lights */}
          <div className="absolute top-0 left-0 w-full h-2 flex justify-around">
            {Array(7).fill().map((_, i) => (
              <div 
                key={`top-light-${i}`} 
                className={`w-2 h-2 rounded-full ${
                  i % 3 === 0 ? "bg-red-500" : 
                  i % 3 === 1 ? "bg-blue-500" : 
                  "bg-yellow-500"
                } animate-cabinet-light`}
                style={{ animationDelay: `${i * 0.2}s` }}
              ></div>
            ))}
          </div>
          
          <div className="absolute bottom-0 left-0 w-full h-2 flex justify-around">
            {Array(7).fill().map((_, i) => (
              <div 
                key={`bottom-light-${i}`} 
                className={`w-2 h-2 rounded-full ${
                  i % 3 === 0 ? "bg-green-500" : 
                  i % 3 === 1 ? "bg-pink-500" : 
                  "bg-purple-500"
                } animate-cabinet-light`}
                style={{ animationDelay: `${i * 0.2}s` }}
              ></div>
            ))}
          </div>
          
          <div className="absolute left-0 top-0 h-full w-2 flex flex-col justify-around">
            {Array(5).fill().map((_, i) => (
              <div 
                key={`left-light-${i}`} 
                className={`w-2 h-2 rounded-full ${
                  i % 3 === 0 ? "bg-yellow-500" : 
                  i % 3 === 1 ? "bg-pink-500" : 
                  "bg-blue-500"
                } animate-cabinet-light`}
                style={{ animationDelay: `${i * 0.3}s` }}
              ></div>
            ))}
          </div>
          
          <div className="absolute right-0 top-0 h-full w-2 flex flex-col justify-around">
            {Array(5).fill().map((_, i) => (
              <div 
                key={`right-light-${i}`} 
                className={`w-2 h-2 rounded-full ${
                  i % 3 === 0 ? "bg-green-500" : 
                  i % 3 === 1 ? "bg-purple-500" : 
                  "bg-red-500"
                } animate-cabinet-light`}
                style={{ animationDelay: `${i * 0.3}s` }}
              ></div>
            ))}
          </div>

          {/* Paytable Overlay */}
          {showPaytable && (
            <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
              <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/80 backdrop-blur-lg rounded-lg p-3 sm:p-4 w-full max-w-sm sm:max-w-md mx-auto border-2 border-gray-700 max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
                  <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">Fruit Scatter Paytable</h2>
                  <button 
                    onClick={() => {
                      playSound('BUTTON_CLICK');
                      setShowPaytable(false);
                    }}
                    className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-gray-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Vegas-style payline visualization */}
                {/* <div className="mb-4 bg-gray-900/50 p-3 rounded-lg border border-gray-700">
                  <h3 className="text-sm sm:text-base font-bold text-gray-300 mb-2">Paylines</h3>
                  <div className="grid grid-cols-5 gap-1 mb-2">
                    {Array(10).fill().map((_, lineId) => (
                      <div 
                        key={`payline-${lineId}`}
                        className={`bg-gradient-to-r ${paylines[lineId].color} rounded-md p-0.5 flex items-center justify-center text-white font-bold text-xs`}
                      >
                        {lineId + 1}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Match 3 or more symbols on any of the above paylines to win!
                  </p>
                </div> */}
                
                <div className="grid grid-cols-1 gap-2">
                  {symbols.map(symbol => (
                    <div key={symbol.name} className="flex items-center p-2 bg-gray-800/50 rounded-lg shadow-md hover:bg-gray-700/50 transition-all duration-200 border border-gray-700">
                      <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg ${symbolStyles[symbol.name].bg} border-2 ${symbolStyles[symbol.name].border}`}>
                      {symbol.name === "scatter" ? (
                        <img 
                          src={symbolStyles[symbol.name].imageSrc} 
                          alt="Scatter Star" 
                          className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                        />
                      ) : (
                        <span className="text-xl sm:text-2xl">{symbolStyles[symbol.name].icon}</span>
                      )}
                      </div>
                      <div className="ml-3 sm:ml-4 flex-1">
                        <div className="font-bold text-gray-200 capitalize text-sm sm:text-base">{symbol.name}</div>
                        <div className="text-xs sm:text-sm text-gray-300 flex flex-wrap gap-x-3">
                          {symbol.value[3] > 0 && (
                            <span className="flex items-center">
                              <span className="inline-block w-6 text-gray-400">3x:</span>
                              <span className="font-mono text-yellow-400">{symbol.value[3]}x</span>
                            </span>
                          )}
                          {symbol.value[4] > 0 && (
                            <span className="flex items-center">
                              <span className="inline-block w-6 text-gray-400">4x:</span>
                              <span className="font-mono text-yellow-400">{symbol.value[4]}x</span>
                            </span>
                          )}
                          {symbol.value[5] > 0 && (
                            <span className="flex items-center">
                              <span className="inline-block w-6 text-gray-400">5x:</span>
                              <span className="font-mono text-yellow-400">{symbol.value[5]}x</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 text-gray-300 bg-gradient-to-r from-gray-800/50 to-gray-900/50 p-3 rounded-lg text-xs sm:text-sm border border-gray-700 shadow-lg">
                  <p className="mb-2"><span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-500 to-pink-500 font-bold">Wild Symbol (🃏):</span> Substitutes for any symbol except Scatter</p>
                  <p className="mb-2">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-red-400 font-bold">
                      Scatter Symbol
                      <img src={symbolStyles["scatter"].imageSrc} alt="Scatter" className="inline-block h-4 w-4 ml-1 align-middle" />
                      :
                    </span> 
                    3+ Scatters anywhere award Free Spins
                  </p>
                  <p>Find 3 or more matching symbols on a payline to win. Payline wins multiply your bet.</p>
                </div>
                
                {/* Casino return certification */}
                <div className="mt-4 border border-yellow-800 bg-yellow-900/20 p-2 rounded-lg flex items-center">
                  <span className="h-6 w-6 bg-yellow-600 rounded-full flex items-center justify-center mr-2 text-yellow-200 text-xs">
                    96%
                  </span>
                  <span className="text-xs text-yellow-200">
                    This game has a verified return to player of 96.2%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* User Stats Overlay - Enhanced VIP edition */}
          {showUserStats && (
            <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
              <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/80 backdrop-blur-lg rounded-lg p-3 sm:p-4 w-full max-w-sm sm:max-w-md mx-auto border-2 border-gray-700 max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
                  <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-pink-400 to-red-400">VIP Player Profile</h2>
                  <button 
                    onClick={() => {
                      playSound('BUTTON_CLICK');
                      setShowUserStats(false);
                    }}
                    className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-gray-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* User Profile Preview with VIP status */}
                <div className="flex flex-col items-center mb-4 relative">
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r ${getCurrentVipTier().color} px-3 py-0.5 rounded-full text-xs text-white font-bold shadow-lg border border-white/20 z-10`}>
                    {getCurrentVipTier().name} VIP
                  </div>
                  
                  <div className="p-0.5 rounded-full bg-gradient-to-r from-violet-400 to-pink-400 mt-2">
                    <div className="p-0.5 rounded-full bg-gray-900">
                      <Avatar
                        seed={user?.referralCode || user?.emailPrefix || 'guest'}
                        size={80}
                        className="rounded-full"
                      />
                    </div>
                  </div>
                  <div className="mt-2 text-base sm:text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                    {user?.emailPrefix || 'Guest'}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300">
                    {formatCurrency(user?.chips || 0)} Chips
                  </div>
                  
                  {/* Vegas-style VIP progress bar */}
                  <div className="mt-3 w-full bg-gray-800 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full bg-gradient-to-r ${vipTiers[Math.min(vipLevel + 1, 5)].color}`}
                      style={{ width: `${vipLevel < 5 ? (60 + vipLevel * 8) : 100}%` }}
                    ></div>
                  </div>
                  <div className="w-full flex justify-between text-xs text-gray-400 mt-1">
                    <span>{vipLevel < 5 ? `${(100 - (vipLevel + 1) * 20)}% to ${vipTiers[Math.min(vipLevel + 1, 5)].name}` : 'MAX LEVEL'}</span>
                    <span>{vipTiers[vipLevel].bonus} Bonus Active</span>
                  </div>
                </div>
                
                {/* Casino Stats in Vegas style */}
                <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg shadow-lg mb-3 border border-gray-700">
                  <div className="flex items-center mb-2">
                    <CurrencyDollarIcon className="h-4 w-4 text-yellow-400 mr-1.5" />
                    <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 font-semibold">CASINO STATS</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total Plays:</span>
                      <span className="text-blue-300 font-mono">{user?.totalCasinoPlays || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Win Rate:</span>
                      <span className="text-green-300 font-mono">{getWinRate()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Biggest Win:</span>
                      <span className="text-pink-300 font-mono">{formatCurrency(user?.largestCasinoWin || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Account Age:</span>
                      <span className="text-violet-300 font-mono">{getAccountAge()}</span>
                    </div>
                  </div>
                </div>
                
                {/* Loyalty Rewards section */}
                {/* <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg shadow-lg mb-3 border border-gray-700">
                  <div className="flex items-center mb-2">
                    <TrophyIcon className="h-4 w-4 text-amber-400 mr-1.5" />
                    <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-500 font-semibold">LOYALTY REWARDS</div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-gray-900/60 p-2 rounded-lg border border-gray-800">
                      <div className="flex items-center">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs mr-2">
                          ✓
                        </div>
                        <div>
                          <div className="text-xs text-white">Daily Bonus</div>
                          <div className="text-xs text-gray-400">100 free chips</div>
                        </div>
                      </div>
                      <button className="text-xs bg-green-600 hover:bg-green-500 text-white px-2 py-1 rounded font-semibold transition-colors">
                        Claimed
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between bg-gray-900/60 p-2 rounded-lg border border-gray-800">
                      <div className="flex items-center">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-r from-amber-500 to-yellow-600 flex items-center justify-center text-white text-xs mr-2">
                          <GiftIcon className="h-3 w-3" />
                        </div>
                        <div>
                          <div className="text-xs text-white">VIP Bonus</div>
                          <div className="text-xs text-gray-400">500 chips + 10 free spins</div>
                        </div>
                      </div>
                      <button 
                        className="text-xs bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-white px-2 py-1 rounded font-semibold transition-colors"
                        onClick={() => {
                          playSound('BUTTON_CLICK');
                          setFreeSpins(prev => prev + 10);
                          setBalance(prev => prev + 500);
                          setShowUserStats(false);
                        }}
                      >
                        Claim
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between bg-gray-900/60 p-2 rounded-lg border border-gray-800">
                      <div className="flex items-center">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-r from-gray-600 to-gray-700 flex items-center justify-center text-white text-xs mr-2">
                          🔄
                        </div>
                        <div>
                          <div className="text-xs text-white">Weekly Cashback</div>
                          <div className="text-xs text-gray-400">10% of losses returned</div>
                        </div>
                      </div>
                      <div className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded font-semibold">
                        3d 12h
                      </div>
                    </div>
                  </div>
                </div> */}
                
                {/* Crypto Stats in Vegas style */}
                <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg shadow-lg border border-gray-700">
                  <div className="flex items-center mb-2">
                    <svg className="h-4 w-4 text-blue-400 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 8h10M9 16h10M19 4H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-500 font-semibold">CRYPTO STATS</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Predictions:</span>
                      <span className="text-blue-300 font-mono">{user?.totalPredictions || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Win Rate:</span>
                      <span className="text-green-300 font-mono">{user?.predictionWinRate ? `${(user.predictionWinRate * 100).toFixed(1)}%` : '0%'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Correct:</span>
                      <span className="text-pink-300 font-mono">{user?.predictionWins || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Profit/Loss:</span>
                      <span className="text-emerald-300 font-mono">+{formatCurrency(238.50)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Responsible gaming */}
                <div className="mt-4 border-t border-gray-700 pt-4">
                  <div className="flex items-center justify-center text-xs text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Play Responsibly • Must be 18+ to play
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fairness Modal - Enhanced quantum styling */}
          {showFairnessModal && (
            <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
              <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/80 backdrop-blur-lg rounded-lg p-3 sm:p-4 w-full max-w-sm sm:max-w-md mx-auto border-2 border-gray-700 max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
                  <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400">Quantum Fairness</h2>
                  <button 
                    onClick={() => {
                      playSound('BUTTON_CLICK');
                      setShowFairnessModal(false);
                    }}
                    className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-gray-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Quantum visualization effect */}
                <div className="mb-4 flex justify-center">
                  <div className="relative w-24 h-24">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full opacity-20 animate-pulse"></div>
                    <div className="absolute inset-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full opacity-40 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                    <div className="absolute inset-6 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute inset-9 bg-white rounded-full opacity-80 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BeakerIcon className="h-8 w-8 text-blue-300" />
                    </div>
                  </div>
                </div>
                
                {/* Last Game Proof with enhanced Vegas styling */}
                {fairnessProof && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 mb-2">Last Game Proof</h3>
                    <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg border border-gray-700 text-xs shadow-lg">
                      <div className="grid grid-cols-2 gap-y-2">
                        <div className="text-gray-400">Timestamp:</div>
                        <div className="text-gray-200 font-mono">{new Date(fairnessProof.timestamp).toLocaleString()}</div>
                        
                        <div className="text-gray-400">Bet Amount:</div>
                        <div className="text-yellow-300 font-mono">{formatCurrency(fairnessProof.betAmount)}</div>
                        
                        <div className="text-gray-400">Payout:</div>
                        <div className="text-green-300 font-mono">{formatCurrency(fairnessProof.payout)}</div>
                        
                        <div className="text-gray-400">Method:</div>
                        <div className="text-cyan-400 font-mono">
                          {fairnessProof.quantumSource || 'Quantum RNG'}
                        </div>
                        
                        {fairnessProof.verificationHash && (
                          <div className="col-span-2 mt-3 pt-3 border-t border-gray-700">
                            <div>
                              <span className="text-gray-400">Verification Hash:</span>
                              <span className="text-blue-400 break-all text-xs block mt-1 p-1.5 bg-blue-900/20 rounded border border-blue-900 font-mono overflow-x-auto">
                                {fairnessProof.verificationHash}
                              </span>
                            </div>
                            
                            <div className="mt-2">
                              <span className="text-gray-400">Verify using:</span>
                              <div className="mt-1">
                                <a 
                                  href="https://qrng.anu.edu.au/" 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  Learn more about ANU Quantum RNG
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {fairnessProof.verificationUrl && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Verify quantum randomness:</span>
                            <a 
                              href={fairnessProof.verificationUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 bg-blue-900/20 px-2 py-1 rounded border border-blue-900/50 flex items-center"
                            >
                              <LockClosedIcon className="h-3 w-3 mr-1" />
                              View Proof
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
{/* How Quantum RNG Works - Vegas-style explanation */}
<div>
                  <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 mb-2">How Quantum RNG Works</h3>
                  <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg border border-gray-700 text-xs text-gray-300 space-y-2 shadow-lg">
                    <div className="flex items-start">
                      <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5">
                        1
                      </div>
                      <p>Our game uses true randomness derived from quantum phenomena like photon behavior, which is fundamentally unpredictable by the laws of physics</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="h-5 w-5 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5">
                        2
                      </div>
                      <p>The random numbers are provided by quantum computers at the Australian National University's Quantum Random Number Server</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="h-5 w-5 rounded-full bg-violet-500 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5">
                        3
                      </div>
                      <p>Each spin generates a unique cryptographic hash value that can be independently verified by anyone</p>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="h-5 w-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs mr-2 flex-shrink-0 mt-0.5">
                        4
                      </div>
                      <p>The quantum random values are used to determine the symbols on the reels, ensuring truly unpredictable and fair outcomes that cannot be manipulated</p>
                    </div>
                  </div>
                </div>
                
                {/* Quantum Advantage */}
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 mb-2">The Quantum Advantage</h3>
                  <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-3 rounded-lg border border-blue-900/50 text-xs text-gray-300 space-y-2 shadow-lg">
                    <div className="flex items-center bg-blue-900/30 p-2 rounded">
                      <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white mr-2 shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-blue-400 font-semibold mb-0.5">True Randomness</div>
                        <p>Unlike algorithmic random number generators, quantum randomness is fundamentally unpredictable even with complete knowledge of the system</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center bg-purple-900/30 p-2 rounded">
                      <div className="h-8 w-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white mr-2 shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-purple-400 font-semibold mb-0.5">Scientific Verification</div>
                        <p>Each spin's randomness can be verified by scientists and researchers worldwide using cryptographic hash functions</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center bg-pink-900/30 p-2 rounded">
                      <div className="h-8 w-8 bg-gradient-to-r from-pink-500 to-pink-600 rounded-full flex items-center justify-center text-white mr-2 shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-pink-400 font-semibold mb-0.5">Transparent Process</div>
                        <p>The quantum source, timestamp and verification hash are all stored for each spin and available for independent verification</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Certification badges */}
                {/* <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  <div className="flex items-center bg-gray-800 px-2 py-1 rounded border border-gray-700 text-xs">
                    <img src="/api/placeholder/24/24" alt="Quantum Cert" className="mr-1" />
                    <span className="text-blue-300">Quantum Certified</span>
                  </div>
                  
                  <div className="flex items-center bg-gray-800 px-2 py-1 rounded border border-gray-700 text-xs">
                    <img src="/api/placeholder/24/24" alt="Fair Play" className="mr-1" />
                    <span className="text-green-300">Fair Play Verified</span>
                  </div>
                  
                  <div className="flex items-center bg-gray-800 px-2 py-1 rounded border border-gray-700 text-xs">
                    <img src="/api/placeholder/24/24" alt="RNG Test" className="mr-1" />
                    <span className="text-amber-300">RNG Tested</span>
                  </div>
                </div> */}
              </div>
            </div>
          )}

          {/* Promotions Modal */}
          {showPromotions && (
            <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
              <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/80 backdrop-blur-lg rounded-lg p-3 sm:p-4 w-full max-w-sm sm:max-w-md mx-auto border-2 border-purple-700 max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
                  <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500">Vegas Promotions</h2>
                  <button 
                    onClick={() => {
                      playSound('BUTTON_CLICK');
                      setShowPromotions(false);
                    }}
                    className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-gray-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Featured promotion */}
                <div className="mb-4 bg-gradient-to-r from-yellow-900 to-amber-900 p-0.5 rounded-lg">
                  <div className="bg-gradient-to-b from-yellow-900/60 to-amber-900/60 rounded-lg overflow-hidden">
                    <div className="h-32 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 relative flex items-center justify-center">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-400 font-bold animate-pulse">
                          200% WELCOME BONUS
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black to-transparent"></div>
                    </div>
                    <div className="p-3">
                      <div className="text-amber-300 font-bold mb-1">Welcome Package</div>
                      <div className="text-gray-300 text-sm mb-2">
                        Deposit now and get a 200% matching bonus up to $500 plus 50 free spins!
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            playSound('BUTTON_CLICK');
                            window.location.href = '/buy-chips';
                            // Would open deposit modal in real implementation
                            setShowPromotions(false);
                          }}
                          className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 rounded text-sm font-bold text-white transition-colors shadow-lg shadow-amber-700/20"
                        >
                          DEPOSIT NOW
                        </button>
                        <button 
                          className="py-2 px-3 bg-gray-800 hover:bg-gray-700 rounded text-sm font-bold text-gray-300 border border-gray-700 transition-colors"
                          onClick={() => {
                            playSound('BUTTON_CLICK');
                            // Show terms in real implementation
                          }}
                        >
                          T&C
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Additional promotions */}
                <div className="space-y-3">
                  {/* Free Spins Promotion */}
                  {/* <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 p-3 rounded-lg border border-purple-800/50">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center mr-3 text-lg">
                        🎮
                      </div>
                      <div>
                        <div className="text-indigo-300 font-bold">Weekend Spin Boost</div>
                        <div className="text-xs text-gray-400">Available every weekend</div>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs mb-2">
                      Get 20 free spins when you play on weekends! Just log in on Saturday or Sunday.
                    </div>
                    <button 
                      onClick={() => {
                        playSound('BUTTON_CLICK');
                        setFreeSpins(prev => prev + 20);
                        setShowPromotions(false);
                      }}
                      className="w-full py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded text-xs font-bold text-white shadow-lg"
                    >
                      CLAIM 20 FREE SPINS
                    </button>
                  </div> */}
                  
                  {/* VIP Cashback */}
                  {/* <div className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 p-3 rounded-lg border border-blue-800/50">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-center mr-3 text-lg">
                        💎
                      </div>
                      <div>
                        <div className="text-blue-300 font-bold">VIP Cashback</div>
                        <div className="text-xs text-gray-400">Exclusive for {vipTiers[vipLevel].name}+ members</div>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs mb-2">
                      {vipTiers[vipLevel].name} members receive {vipTiers[vipLevel].bonus} cashback on all losses, automatically credited every Monday.
                    </div>
                    <div className="w-full py-1.5 bg-gradient-to-r from-cyan-900 to-blue-900 rounded text-xs font-bold text-center text-cyan-300 border border-cyan-800">
                      AUTO-ACTIVATED
                    </div>
                  </div> */}
                  
                  {/* Daily Bonus */}
                  <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 p-3 rounded-lg border border-green-800/50">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 flex items-center justify-center mr-3 text-lg">
                        🎁
                      </div>
                      <div>
                        <div className="text-green-300 font-bold">Daily Bonus Chips</div>
                        <div className="text-xs text-gray-400">
                          {isDailyBonusClaimed && nextDailyBonusTime
                            ? bonusCountdownText
                            : "Claim once every 24 hours"}
                        </div>
                      </div>
                    </div>
                    <div className="text-gray-300 text-xs mb-2">
                      Claim your free 100 chips every day. Never miss a day to maintain your streak!
                    </div>
                    <button 
                      onClick={handleClaimDailyBonus}
                      disabled={isDailyBonusClaimed}
                      className={`w-full py-1.5 rounded text-xs font-bold shadow-lg ${
                        isDailyBonusClaimed
                          ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white"
                      }`}
                    >
                      {isDailyBonusClaimed 
                        ? `CLAIMED TODAY`
                        : `CLAIM 100 CHIPS`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Game grid with enhanced casino styling */}
          <div className="bg-gradient-to-b from-gray-900/90 to-black/90 p-3 sm:p-4 relative">
            {/* Cabinet top display */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600"></div>
            
            {/* Slot window with metallic frame */}
            <div className="p-1 rounded-lg bg-gradient-to-b from-gray-600 via-gray-700 to-gray-800 shadow-inner border border-gray-700">
              <div className="bg-black rounded-lg p-1.5 relative overflow-hidden">
                {/* Scan lines effect */}
                <div className="absolute inset-0 pointer-events-none animate-scanline slot-scanline opacity-20"></div>
                
                {/* Reels container */}
                <div 
                  ref={reelsRef}
                  className="grid grid-cols-5 gap-1 relative"
                >
                  {/* Column divider lines - classic slot machine style */}
                  {Array(4).fill().map((_, i) => (
                    <div 
                      key={`divider-${i}`}
                      className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-gray-700/20 via-gray-600/40 to-gray-700/20"
                      style={{ left: `${(i + 1) * 20}%` }}
                    ></div>
                  ))}
                  
                  {/* Payline overlay indicators */}
                  {visiblePaylines.map(lineId => {
                    const payline = paylines.find(p => p.id === lineId);
                    if (!payline) return null;
                    
                    return (
                      <div 
                        key={`payline-vis-${lineId}`}
                        className={`absolute inset-0 z-30 pointer-events-none`}
                      >
                        <svg className="w-full h-full" viewBox="0 0 500 300">
                          <path 
                            d={payline.positions.map((pos, i) => {
                              // Calculate position in viewBox coordinates
                              const x = (pos[1] * 100) + 50;
                              const y = (pos[0] * 100) + 50;
                              return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                            }).join(' ')}
                            className={`stroke-[5] fill-none stroke-[${payline.color}] animate-pulse`}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="10,5"
                          />
                        </svg>
                      </div>
                    );
                  })}
                  
                  {/* Actual grid symbols */}
                  {grid.map((row, rowIdx) => 
                    row.map((symbol, colIdx) => {
                      const isWinning = isWinningPosition(rowIdx, colIdx);
                      const isPayline = isPaylinePosition(rowIdx, colIdx);
                      const paylineColor = getPaylineColor(rowIdx, colIdx);
                      
                      return (
                        <div 
                          key={`${rowIdx}-${colIdx}`}
                          className={`aspect-square flex items-center justify-center rounded-lg overflow-hidden relative border-2 shadow-lg
                            ${isWinning 
                              ? "border-pink-500 shadow-pink-500/50 animate-pulse" 
                              : isPayline
                                ? `border-${paylineColor}`
                                : `${symbolStyles[symbol].border} border-opacity-50`}`}
                        >
                          {/* Symbol background */}
                          <div className={`absolute inset-0 ${symbolStyles[symbol].bg} ${
                            spinning ? "animate-pulse" : ""
                          } ${isWinning ? "opacity-100" : "opacity-90"}`}></div>
                          
                          {/* Blur effect during spinning */}
                          {spinning && (
                            <div className="absolute inset-0 backdrop-blur-sm"></div>
                          )}
                          
                          {/* Win highlight effects */}
                          {isWinning && (
                            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-amber-500 opacity-40 z-10 animate-pulse">
                              <div className="absolute inset-0 bg-white opacity-40 animate-ping"></div>
                              <div className="absolute top-0 left-0 w-2 h-2 bg-white rounded-full animate-ping delay-75"></div>
                              <div className="absolute bottom-0 right-0 w-2 h-2 bg-white rounded-full animate-ping delay-150"></div>
                              <div className="absolute top-0 right-0 w-2 h-2 bg-white rounded-full animate-ping delay-300"></div>
                              <div className="absolute bottom-0 left-0 w-2 h-2 bg-white rounded-full animate-ping delay-500"></div>
                              
                              {/* Additional particle effects for winning symbols */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-full h-full relative overflow-hidden">
                                  {Array(5).fill().map((_, i) => (
                                    <div 
                                      key={`particle-${rowIdx}-${colIdx}-${i}`}
                                      className="absolute w-1 h-1 bg-white rounded-full animate-ping"
                                      style={{
                                        top: `${Math.random() * 100}%`,
                                        left: `${Math.random() * 100}%`,
                                        animationDelay: `${Math.random() * 1}s`
                                      }}
                                    ></div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Payline highlight */}
                          {isPayline && !isWinning && (
                            <div className={`absolute inset-0 bg-gradient-to-r ${paylineColor} opacity-30 z-5 animate-pulse`}></div>
                          )}
                          
                          {/* Symbol */}
                          <div className={`relative z-20 text-2xl sm:text-3xl ${
                            spinning 
                              ? "animate-spin blur-sm" 
                              : isWinning
                                ? "animate-bounce"
                                : symbol === "scatter" || symbol === "wild" 
                                  ? "animate-pulse" 
                                  : ""
                          }`}
                          style={{
                            // Add 3D effect for symbols
                            textShadow: isWinning 
                              ? "0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.4)" 
                              : "0 2px 5px rgba(0,0,0,0.5)"
                          }}
                          >
                            {symbolStyles[symbol].icon === "scatter-star" ? (
                              <img 
                                src={symbolStyles[symbol].imageSrc} 
                                alt="Scatter Star" 
                                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                              />
                            ) : (
                              symbolStyles[symbol].icon
                            )}
                          </div>
                          
                          {/* Bottom highlight for 3D effect */}
                          <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            
            {/* Win lines display */}
            {winLines.length > 0 && (
              <div className="mt-2 flex justify-center">
                <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 rounded-full px-3 py-1 text-xs font-semibold text-pink-300 border border-pink-900/50 animate-pulse">
                  {winLines.length} Active Win Line{winLines.length !== 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Controls Section with Vegas styling */}
          <div className="bg-gradient-to-b from-gray-900/80 to-black/80 p-3 sm:p-5 border-t-4 border-gray-800 backdrop-blur-sm relative">
            {/* Decorative lights around controls */}
            <div className="absolute top-0 left-0 right-0 flex justify-around">
              {Array(10).fill().map((_, i) => (
                <div 
                  key={`control-light-${i}`}
                  className={`w-1 h-1 rounded-full ${
                    i % 5 === 0 ? "bg-red-500" : 
                    i % 5 === 1 ? "bg-blue-500" : 
                    i % 5 === 2 ? "bg-green-500" : 
                    i % 5 === 3 ? "bg-yellow-500" : 
                    "bg-purple-500"
                  } animate-pulse`}
                  style={{ animationDelay: `${i * 0.3}s` }}
                ></div>
              ))}
            </div>
            
{/* Main controls area with better spacing */}
<div className="flex flex-col space-y-3 sm:space-y-4">
  {/* Bet control row with Vegas styling */}
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-1 sm:space-x-2 flex-1 mr-2 sm:mr-3">
      <button 
        onClick={() => adjustBet(-0.10)}
        disabled={spinning || betAmount <= 0.20 || freeSpins > 0}
        className="bg-gradient-to-br from-violet-800 to-violet-900 hover:from-violet-700 hover:to-violet-800 rounded-lg h-9 w-9 sm:h-11 sm:w-11 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed border border-violet-700/50 shadow-lg shadow-violet-900/20 transition-all duration-300 transform hover:scale-105"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      </button>
      
      <div className="group bg-gradient-to-b from-gray-900 to-gray-950 rounded-lg px-3 sm:px-4 py-1 sm:py-2 flex-1 border border-gray-800/80 shadow-inner shadow-gray-950 hover:border-violet-700/30 transition-colors transform">
        <div className="text-xs text-gray-400 uppercase tracking-wider">Amount</div>
        
        {/* Improved Vegas-style input */}
        <div className="relative">
          {/* Hidden actual input - for data management */}
          <input
            type="text"
            inputMode="decimal"
            value={freeSpins > 0 && freeSpinBetAmount ? freeSpinBetAmount : betAmount}
            onChange={(e) => {
              // Prevent changes if free spins are active
              if (freeSpins > 0) return;
              
              // Allow only numbers and decimals
              const value = e.target.value.replace(/[^0-9.]/g, '');
              
              // Ensure only one decimal point
              const parts = value.split('.');
              const cleanValue = parts[0] + (parts.length > 1 ? '.' + parts[1] : '');
              
              // Only update if it's a valid number or empty
              if (cleanValue === '' || cleanValue === '.') {
                  setBetAmount(cleanValue);
              } else {
                  const numValue = parseFloat(cleanValue);
                  if (!isNaN(numValue)) {
                      // Ensure bet is within allowed range during typing
                      const validBet = Math.max(0.20, Math.min(20, numValue));
                      setBetAmount(validBet);
                  }
              }
            }}
            onBlur={(e) => {
              // Prevent changes if free spins are active
              if (freeSpins > 0) return;
              
              // Validate range on blur
              const value = parseFloat(e.target.value);
              if (isNaN(value) || value < 0.20) {
                setBetAmount(0.20);
              } else if (value > 20) {
                setBetAmount(20);
              } else {
                // Ensure two decimal places
                setBetAmount(parseFloat(value.toFixed(2)));
              }
            }}
            onClick={() => playSound('BUTTON_CLICK')}
            disabled={spinning || freeSpins > 0}
            className="w-full bg-transparent border-none outline-none text-base sm:text-xl font-bold text-violet-400 focus:text-pink-400 transition-colors focus:ring-0 appearance-none disabled:opacity-70"
            style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}
          />
          
          {/* Display a "locked" indicator if free spins are active */}
          {freeSpins > 0 && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2 text-xs text-indigo-400 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Locked</span>
            </div>
          )}
          
          {/* Vegas-style glowing effect for the input */}
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-pink-500/10 rounded-md pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </div>
      </div>
      
      <button 
        onClick={() => adjustBet(0.10)}
        disabled={spinning || betAmount >= 20 || freeSpins > 0}
        className="bg-gradient-to-br from-violet-800 to-violet-900 hover:from-violet-700 hover:to-violet-800 rounded-lg h-9 w-9 sm:h-11 sm:w-11 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed border border-violet-700/50 shadow-lg shadow-violet-900/20 transition-all duration-300 transform hover:scale-105"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
    
    <button 
      onClick={maxBet}
      disabled={spinning || balance < 20 || freeSpins > 0}
      className="py-2 sm:py-3 px-3 sm:px-4 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-500 hover:to-yellow-400 text-white font-bold rounded-lg text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 border border-amber-500/50 shadow-lg shadow-amber-700/30 whitespace-nowrap transform hover:scale-105"
    >
      MAX BET
      <span className="ml-1 text-yellow-200">20</span>
    </button>
  </div>
  
  {/* Spin button with enhanced casino styling */}
  <button 
    onClick={() => freeSpins > 0 ? handleSpin(true) : handleSpin(false)}
    // onClick={() => {}}
    disabled={spinning || (freeSpins === 0 && balance < betAmount)}
    className={`w-full py-3 sm:py-5 rounded-lg font-bold text-lg sm:text-xl tracking-wider transition-all duration-300 ${
      spinning
        ? "bg-gradient-to-r from-gray-800 to-gray-900 cursor-not-allowed text-gray-500 border-2 border-gray-700 shadow-inner"
        : freeSpins > 0
          ? "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-xl shadow-indigo-600/30 border-2 border-indigo-500/50 transform hover:scale-[1.02]"
          : "bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-xl shadow-blue-600/30 border-2 border-blue-500/50 transform hover:scale-[1.02]"
    } flex items-center justify-center relative overflow-hidden`}
  >
    {spinning ? (
      <>
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/30 to-blue-600/30 animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent h-1/2"></div>
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          <svg className="animate-spin mr-2 h-5 w-5 sm:h-6 sm:w-6 text-cyan-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="relative z-10 flex items-center">
            <BeakerIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-cyan-300" />
            SPINNING...
          </span>
        </div>
      </>
    ) : (
      <>
        {/* Button shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full animate-shine pointer-events-none"></div>
        
        {/* Button 3D effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent h-1/2 pointer-events-none"></div>
        
        {freeSpins > 0 ? (
          <div className="flex items-center">
            <SpeakerWaveIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-indigo-300" />
            FREE SPIN ({freeSpins}) 
            {freeSpinBetAmount && freeSpinBetAmount !== betAmount && (
              <span className="ml-2 text-xs bg-indigo-700 px-2 py-0.5 rounded-full text-indigo-200">
                Bet: {freeSpinBetAmount.toFixed(2)}
              </span>
            )}
            <span className="ml-2 animate-pulse">⭐</span>
          </div>
        ) : (
          <div className="flex items-center">
            <BeakerIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-cyan-300" />
            SPIN
          </div>
        )}
      </>
    )}
  </button>

  {/* Bottom utility buttons with improved Vegas styling */}
  <div className="flex flex-col gap-2">
  {/* First row with 2 buttons */}
  <div className="flex items-center justify-between gap-2">
    <button 
      onClick={() => {
        playSound('BUTTON_CLICK');
        setShowPaytable(true);
      }}
      className="flex-1 flex items-center justify-center py-1.5 sm:py-2 px-2 sm:px-3 bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-violet-300 hover:text-violet-200 rounded-lg text-xs sm:text-sm border border-gray-700/50 transition-all duration-300 shadow-lg transform hover:scale-105"
    >
      <InformationCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" />
      Paytable
    </button>
    
    <button 
      onClick={() => {
        playSound('BUTTON_CLICK');
        setShowPromotions(true);
      }}
      className="flex-1 flex items-center justify-center py-1.5 sm:py-2 px-2 sm:px-3 bg-gradient-to-br from-amber-800 to-yellow-900 hover:from-amber-700 hover:to-yellow-800 text-yellow-300 hover:text-yellow-200 rounded-lg text-xs sm:text-sm border border-yellow-700/50 transition-all duration-300 shadow-lg transform hover:scale-105"
    >
      <GiftIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" />
      Offers
    </button>
  </div>
  
  {/* Second row with 2 buttons */}
  <div className="flex items-center justify-between gap-2">
    <button 
      onClick={() => {
        playSound('BUTTON_CLICK');
        setShowVerificationHistory(true);
      }}
      className="flex-1 flex items-center justify-center py-1.5 sm:py-2 px-2 sm:px-3 bg-gradient-to-br from-blue-800 to-cyan-900 hover:from-blue-700 hover:to-cyan-800 text-cyan-300 hover:text-cyan-200 rounded-lg text-xs sm:text-sm border border-blue-700/50 transition-all duration-300 shadow-lg transform hover:scale-105"
    >
      <BeakerIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" />
      Verify
    </button>

    <button 
      onClick={() => {
        playSound('BUTTON_CLICK');
        setShowPayline(true);
      }}
      className="flex-1 flex items-center justify-center py-1.5 sm:py-2 px-2 sm:px-3 bg-gradient-to-br from-blue-800 to-cyan-900 hover:from-blue-700 hover:to-cyan-800 text-cyan-300 hover:text-cyan-200 rounded-lg text-xs sm:text-sm border border-blue-700/50 transition-all duration-300 shadow-lg transform hover:scale-105"
    >
      <BeakerIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" />
      View Payline
    </button>
  </div>
</div>
</div>
            
            {/* Responsible gaming footer */}
            <div className="mt-4 pt-2 border-t border-gray-800/50 flex justify-center">
              <div className="text-xs text-gray-500 flex items-center">
                <LockClosedIcon className="h-3 w-3 mr-1" />
                Play Responsibly • Quantum Secured
              </div>
            </div>
          </div>

          {/* Casino certification badges */}
          {/* <div className="bg-black/60 p-2 flex justify-center space-x-3 border-t border-gray-800">
            <div className="flex items-center bg-gray-900/50 p-1 rounded-md">
              <img src="/api/placeholder/16/16" alt="Fair Play" className="mr-1" />
              <span className="text-xs text-gray-400">Fair Play</span>
            </div>
            <div className="flex items-center bg-gray-900/50 p-1 rounded-md">
              <img src="/api/placeholder/16/16" alt="Quantum" className="mr-1" />
              <span className="text-xs text-gray-400">Quantum Verified</span>
            </div>
            <div className="flex items-center bg-gray-900/50 p-1 rounded-md">
              <img src="/api/placeholder/16/16" alt="Secure" className="mr-1" />
              <span className="text-xs text-gray-400">SSL Secure</span>
            </div>
          </div> */}
          
          {/* Error notification - Vegas style */}
          {error && (
            <div className="bg-red-900/80 p-2 sm:p-3 text-center text-white text-xs sm:text-sm border-t border-red-700 animate-pulse">
              <div className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-red-300 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {error}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add the floating notification here, before any modals */}
      {/* {showLostFeedback && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-900 to-green-900 text-emerald-300 rounded-full px-4 py-2 shadow-lg border border-emerald-700 z-50 text-sm flex items-center animate-bounce">
          <WalletIcon className="h-4 w-4 mr-2 text-emerald-400" />
          {formatCurrency(lastLostAmount)} added to Community Pot
        </div>
      )} */}

      {/* Verification History Modal */}
      <VerificationHistoryModal 
        isOpen={showVerificationHistory}
        onClose={() => {
          playSound('BUTTON_CLICK');
          setShowVerificationHistory(false);
        }}
      />

{/* Render the visualizer only when showPayline is true */}
{showPayline && (
  <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
    <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/80 backdrop-blur-lg rounded-lg p-3 sm:p-4 w-full max-w-4xl mx-auto border-2 border-gray-700 max-h-[90vh] overflow-y-auto shadow-xl">
      <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
        <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400">Payline Visualizer</h2>
        <button 
          onClick={() => {
            playSound('BUTTON_CLICK');
            setShowPayline(false);
          }}
          className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-gray-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Render the component itself */}
      <SleekPaylinesVisualizer />
    </div>
  </div>
)}

      {/* Community Pot Modal */}
      {showCommunityPotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-2 sm:p-4 overflow-auto">
          <div className="bg-gradient-to-b from-gray-900/80 to-gray-800/80 backdrop-blur-lg rounded-lg p-3 sm:p-4 w-full max-w-sm sm:max-w-md mx-auto border-2 border-green-700 max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center mb-3 sm:mb-4 sticky top-0 bg-gray-900/90 p-1 z-10">
              <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-400 to-teal-500">Community Pot</h2>
              <button 
                onClick={() => {
                  playSound('BUTTON_CLICK');
                  setShowCommunityPotModal(false);
                }}
                className="text-gray-300 hover:text-white bg-gray-800 p-1.5 sm:p-2 rounded-full hover:bg-gray-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Pot Amount Display */}
            <div className="mb-4 relative">
              <div className="bg-gradient-to-b from-green-900/40 to-emerald-900/40 rounded-lg p-4 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 animate-pulse"></div>
                
                <h3 className="text-emerald-400 text-sm mb-1">CURRENT POT</h3>
                <div className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300">
                  {communityPotAmount.toLocaleString()} {communityPotAmount === 1 ? "Chip" : "Chips"}
                </div>
                
                <div className="mt-3 text-xs text-gray-400">
                  The community pot collects chips from lost bets.
                </div>
              </div>
            </div>
            
            {/* Pot Information */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg border border-gray-700 text-xs text-gray-300 space-y-2 shadow-lg">
              <p>
                Every time a player loses a bet, the bet amount is added to this community pot. 
              </p>
              
              <div className="border-t border-gray-700 pt-2 mt-2">
                <p className="text-xs text-emerald-300 mb-1 font-semibold">How it works:</p>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                    <span>When players lose, their bet amount goes into this pot</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                    <span>The pot grows as more players play and lose</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Lost bets display */}
            <div className="mt-4 bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-lg border border-gray-700 text-center">
              <div className="text-xs text-gray-300">
              Your last lost bet: <span className="text-emerald-400 font-semibold">{lastLostAmount > 0 ? `${lastLostAmount} ${lastLostAmount === 1 ? "Chip" : "Chips"}` : "0 Chips"}</span>
              </div>
            </div>
            
            {/* Close button */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => {
                  playSound('BUTTON_CLICK');
                  setShowCommunityPotModal(false);
                }}
                className="py-2 px-8 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold rounded-lg text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Social proof notifications - Vegas style popup */}
      <LiveWinNotifications />

      {/* Community Jackpot Modal */}
      {/* <CommunityJackpotModal 
        isOpen={showJackpotModal}
        onClose={() => {
          playSound('BUTTON_CLICK');
          setShowJackpotModal(false);
        }}
        jackpotInfo={jackpotInfo}
        userContributions={userJackpotContributions}
      /> */}
    </div>
  );
}
